/**
 * LaunchpadDashboardPage
 *
 * Dashboard for viewing and managing a single launch.
 * Shows status, settings, assets, and allowlist.
 * Allows editing for launches in draft status.
 *
 * Accessible via: /launchpad/:id (not in nav bar yet)
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { DefaultPageLayout } from '../ui/layouts/DefaultPageLayout';
import { Button } from '../ui/components/Button';
import { TextField } from '../ui/components/TextField';
import { TextArea } from '../ui/components/TextArea';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { useAuthStore } from '../store/authStore';
import {
  getLaunch,
  updateLaunch,
  deleteLaunch,
  submitForApproval,
  createFeePayment,
  checkFeePaymentStatus,
  createAuthorizationRequest,
  checkAuthorizationStatus,
  checkAuthorizationOnXRPL,
  uploadLaunchThumbnail,
  uploadLaunchBanner,
  deleteLaunchBanner,
  updateBannerPosition,
  getLaunchAssets,
  clearLaunchAssets,
  getAllowlist,
  addToAllowlist,
  removeFromAllowlist,
  clearAllowlist,
  updateAllowlistEntry,
  previewAllowlistImport,
  bulkDeleteFromAllowlist,
  bulkUpdateAllowlistLimit,
  getAllowlist2,
  addToAllowlist2,
  removeFromAllowlist2,
  clearAllowlist2,
  updateAllowlist2Entry,
  previewAllowlist2Import,
  bulkDeleteFromAllowlist2,
  bulkUpdateAllowlist2Limit,
  activateLaunch,
  pauseLaunch,
  resumeLaunch,
  advancePhase,
  getLaunchRevenue,
  getLaunchSettlements,
  exportLaunchSettlements,
  getLaunchRoyalties,
  validateLaunch,
  type LaunchWithStats,
  type LaunchItem,
  type AllowlistEntry,
  type AddAllowlistEntry,
  type UpdateLaunchInput,
  type FeePaymentPayload,
  type AuthorizationPayload,
  type RevenueStats,
  type LaunchSettlement,
  type LaunchRoyaltyStats,
  type AllowlistFilterStatus,
  type AllowlistStats,
  type AllowlistImportPreview,
  type LaunchValidationResult,
  type RegistrationRequirement,
  type RequirementType,
  getStatusDisplay,
  formatMintPrice,
  dropsToXrpTenths,
  xrpTenthsToDrops,
  getLaunchReferralConfig,
  updateLaunchReferralConfig,
} from '../api/launches';
import { WalletSigningModal } from '../components/transactions/WalletSigningModal';
import { toSigningPayload } from '../lib/wallet';
import { useAssetUpload } from '../hooks/useAssetUpload';
import * as SubframeCore from '@subframe/core';
import {
  FeatherArrowLeft,
  FeatherTrash2,
  FeatherUpload,
  FeatherSave,
  FeatherSend,
  FeatherImage,
  FeatherPlay,
  FeatherPause,
  FeatherClock,
  FeatherDollarSign,
  FeatherExternalLink,
  FeatherCopy,
  FeatherCheck,
  FeatherX,
  FeatherAlertTriangle,
  FeatherChevronDown,
  FeatherZap,
  FeatherPlus,
} from '@subframe/core';
import { DropdownMenu } from '../ui/components/DropdownMenu';
import { WizardStepComposableTraits } from '../components/launchpad/wizard/WizardStepComposableTraits';
import { WizardStepEditions } from '../components/launchpad/wizard/WizardStepEditions';
import { MintRewardsManager } from '../components/launchpad/MintRewardsManager';
import { CollectionSearchSelect } from '../components/launchpad/CollectionSearchSelect';
import { is3DModelUrl, getModelViewerUrl } from '../utils/media';
import '@google/model-viewer';
import { LaunchAmbassadorsTab } from '../components/launchpad/LaunchAmbassadorsTab';

type TabId = 'overview' | 'settings' | 'assets' | 'editions' | 'traits' | 'allowlist' | 'schedule' | 'revenue' | 'royalties' | 'mint-contest' | 'referrals' | 'ambassadors';

export const LaunchpadDashboardPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isAuthenticated, activeWalletAddress } = useAuthStore();

  const [launch, setLaunch] = useState<LaunchWithStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabId>('overview');

  // Edit state
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState<UpdateLaunchInput>({});

  // Referral state
  const [referralEnabled, setReferralEnabled] = useState(false);
  const [referralBps, setReferralBps] = useState<number>(500); // default 5%
  const [referralSaving, setReferralSaving] = useState(false);
  const [referralDirty, setReferralDirty] = useState(false);
  const [referralStats, setReferralStats] = useState<{
    launch_stats?: { referred_mints: number; direct_mints: number; unique_referrers: number; total_referral_fee_drops: number };
    top_referrers?: Array<{ username: string | null; xrpl_address: string; referred_mints: number; total_earned_drops: number }>;
  } | null>(null);

  // Helper to convert ISO string to local datetime-local format
  const formatLocalDateTime = (isoString: string | null | undefined): string => {
    if (!isoString) return '';
    const date = new Date(isoString);
    // Format as YYYY-MM-DDTHH:mm in local timezone
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  // Helper to convert datetime-local value to ISO string (for sending to backend)
  const localDateTimeToISO = (localDateTime: string | null): string | null => {
    if (!localDateTime) return null;
    // datetime-local gives us "YYYY-MM-DDTHH:mm" in local timezone
    // Convert to ISO string which includes timezone info
    const date = new Date(localDateTime);
    return date.toISOString();
  };

  // Get user's timezone for display
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  // Image upload refs
  const thumbnailInputRef = useRef<HTMLInputElement>(null);
  const bannerInputRef = useRef<HTMLInputElement>(null);
  const [uploadingThumbnail, setUploadingThumbnail] = useState(false);
  const [uploadingBanner, setUploadingBanner] = useState(false);
  const [removingBanner, setRemovingBanner] = useState(false);
  const [bannerPositionY, setBannerPositionY] = useState(50);
  const [isDraggingBanner, setIsDraggingBanner] = useState(false);
  const bannerContainerRef = useRef<HTMLDivElement>(null);
  const bannerSaveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Delete confirmation
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Submit confirmation
  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Fee payment state
  const [feePaymentPayload, setFeePaymentPayload] = useState<FeePaymentPayload | null>(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [waitingForPayment, setWaitingForPayment] = useState(false);
  const paymentPollingRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Authorization state
  const [authorizationPayload, setAuthorizationPayload] = useState<AuthorizationPayload | null>(null);
  const [showAuthorizationModal, setShowAuthorizationModal] = useState(false);
  const [authorizationError, setAuthorizationError] = useState<string | null>(null);
  const [authorizingMinter, setAuthorizingMinter] = useState(false);
  const [existingMinterAddress, setExistingMinterAddress] = useState<string | null>(null);

  // Assets state
  const [assets, setAssets] = useState<LaunchItem[]>([]);
  const [assetsLoading, setAssetsLoading] = useState(false);
  const [assetsTotal, setAssetsTotal] = useState(0);
  const [assetsPage, setAssetsPage] = useState(1);
  const ASSETS_PER_PAGE = 50;
  const [selectedAsset, setSelectedAsset] = useState<LaunchItem | null>(null);
  const [clearingAssets, setClearingAssets] = useState(false);

  // Allowlist state
  const [allowlistEntries, setAllowlistEntries] = useState<AllowlistEntry[]>([]);
  const [allowlistLoading, setAllowlistLoading] = useState(false);
  const [allowlistTotal, setAllowlistTotal] = useState(0);
  const [allowlistStats, setAllowlistStats] = useState<AllowlistStats | null>(null);
  const [newAddress, setNewAddress] = useState('');
  const [newMaxMint, setNewMaxMint] = useState('');
  const [addingEntry, setAddingEntry] = useState(false);
  const [clearingAllowlist, setClearingAllowlist] = useState(false);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [addressToRemove, setAddressToRemove] = useState<string | null>(null);
  const [removingEntry, setRemovingEntry] = useState(false);
  const [csvUploading, setCsvUploading] = useState(false);
  const csvInputRef = useRef<HTMLInputElement>(null);

  // Allowlist search/filter/pagination state
  const [allowlistPhase2Selected, setAllowlistPhase2Selected] = useState(false);
  const [allowlistSearch, setAllowlistSearch] = useState('');
  const [allowlistFilter, setAllowlistFilter] = useState<AllowlistFilterStatus>('all');
  const [allowlistPage, setAllowlistPage] = useState(1);
  const [allowlistTotalPages, setAllowlistTotalPages] = useState(1);
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Bulk operations state
  const [selectedAddresses, setSelectedAddresses] = useState<Set<string>>(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [bulkUpdating, setBulkUpdating] = useState(false);
  const [showBulkLimitModal, setShowBulkLimitModal] = useState(false);
  const [bulkMintLimit, setBulkMintLimit] = useState('');

  // Inline editing state
  const [editingAddress, setEditingAddress] = useState<string | null>(null);
  const [editingLimit, setEditingLimit] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);

  // Copy to clipboard state
  const [copiedAllowlistAddress, setCopiedAllowlistAddress] = useState<string | null>(null);
  const [copiedRegLink, setCopiedRegLink] = useState(false);
  const [savingRegistration, setSavingRegistration] = useState(false);
  const [registrationSaved, setRegistrationSaved] = useState(false);

  // Import preview state
  const [showImportPreview, setShowImportPreview] = useState(false);
  const [importPreview, setImportPreview] = useState<AllowlistImportPreview | null>(null);
  const [pendingImportEntries, setPendingImportEntries] = useState<Array<{ wallet_address: string; mint_limit?: number }>>([]);

  // Lifecycle control state
  const [activating, setActivating] = useState(false);
  const [pausing, setPausing] = useState(false);
  const [resuming, setResuming] = useState(false);
  const [showGoLiveConfirm, setShowGoLiveConfirm] = useState(false);
  const [advancingPhase, setAdvancingPhase] = useState(false);
  const [showAdvancePhaseConfirm, setShowAdvancePhaseConfirm] = useState(false);

  // Revenue state
  const [revenueStats, setRevenueStats] = useState<RevenueStats | null>(null);
  const [settlements, setSettlements] = useState<LaunchSettlement[]>([]);
  const [settlementsPage, setSettlementsPage] = useState(1);
  const [settlementsTotalPages, setSettlementsTotalPages] = useState(1);
  const [revenueLoading, setRevenueLoading] = useState(false);
  const [exportingCsv, setExportingCsv] = useState(false);

  // Royalties state
  const [royaltyStats, setRoyaltyStats] = useState<LaunchRoyaltyStats | null>(null);
  const [royaltiesLoading, setRoyaltiesLoading] = useState(false);

  // Validation state (Phase 10 Part 3)
  const [validationResult, setValidationResult] = useState<LaunchValidationResult | null>(null);
  const [validating, setValidating] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [checklistExpanded, setChecklistExpanded] = useState(true);

  const fetchLaunch = useCallback(async () => {
    if (!id || !isAuthenticated) return;

    try {
      setLoading(true);
      setError(null);
      const data = await getLaunch(id);
      setLaunch(data);
      setBannerPositionY(data?.banner_position_y ?? 50);
      setReferralEnabled(data?.referral_enabled ?? false);
      setReferralBps(data?.referral_bps ?? 500);
      setReferralDirty(false);
    } catch (err) {
      console.error('Failed to load launch:', err);
      setError(err instanceof Error ? err.message : 'Failed to load launch');
      // Clear launch data on error to prevent displaying stale data
      // This is important for security - if user switches wallets, we must not show
      // the old launch data from a different wallet's session
      setLaunch(null);
    } finally {
      setLoading(false);
    }
  }, [id, isAuthenticated, activeWalletAddress]);

  useEffect(() => {
    fetchLaunch();
  }, [fetchLaunch]);

  // Fetch referral stats when Referrals tab is active
  useEffect(() => {
    if (activeTab !== 'referrals' || !launch?.id || !launch.referral_enabled) return;
    getLaunchReferralConfig(launch.id).then((config) => {
      if (config.launch_stats) {
        setReferralStats({ launch_stats: config.launch_stats, top_referrers: config.top_referrers });
      }
    }).catch(() => {});
  }, [activeTab, launch?.id, launch?.referral_enabled]);

  // Check for existing minter on another marketplace when authorization is pending
  useEffect(() => {
    if (!launch || launch.status !== 'pending_authorization' || !id) return;
    checkAuthorizationOnXRPL(id).then(result => {
      if (result.currentMinter && result.currentMinter !== result.expectedMinter) {
        setExistingMinterAddress(result.currentMinter);
      } else {
        setExistingMinterAddress(null);
      }
    }).catch(() => {
      // Non-critical check — don't block the page if it fails
    });
  }, [launch?.status, id]);

  // Poll for IPFS progress updates every 10s while processing
  useEffect(() => {
    if (!launch?.ipfs_progress) return;
    const interval = setInterval(async () => {
      if (!id || !isAuthenticated) return;
      try {
        const data = await getLaunch(id);
        setLaunch(data);
      } catch { /* ignore polling errors */ }
    }, 10000);
    return () => clearInterval(interval);
  }, [id, isAuthenticated, !!launch?.ipfs_progress]);

  // Fetch assets when Assets tab is active or page changes
  const fetchAssets = useCallback(async (page = assetsPage) => {
    if (!id || !isAuthenticated) return;

    try {
      setAssetsLoading(true);
      const result = await getLaunchAssets(id, { page, limit: ASSETS_PER_PAGE });
      setAssets(result.items);
      setAssetsTotal(result.pagination.total);
    } catch (err) {
      console.error('Failed to load assets:', err);
    } finally {
      setAssetsLoading(false);
    }
  }, [id, isAuthenticated, assetsPage]);

  useEffect(() => {
    if (activeTab === 'assets') {
      fetchAssets();
    }
  }, [activeTab, fetchAssets]);

  // Asset upload hook
  const {
    progress: uploadProgress,
    errors: uploadErrors,
    isUploading: assetUploading,
    isDragging,
    uploadFolder: handleFolderUpload,
    cancelUpload,
    retryFailed,
    failedCount,
    dragHandlers: assetDragHandlers,
    inputRef: assetsInputRef,
  } = useAssetUpload({
    launchId: id || '',
    clearBeforeUpload: false,
    onComplete: async () => {
      // Refresh assets from page 1 and launch data after upload
      setAssetsPage(1);
      await Promise.all([fetchAssets(1), fetchLaunch()]);
    },
    onError: (msg) => setError(msg),
  });

  // Handle clear assets
  const handleClearAssets = async () => {
    if (!launch) return;

    try {
      setClearingAssets(true);
      await clearLaunchAssets(launch.id);
      setAssets([]);
      setAssetsTotal(0);
      setAssetsPage(1);
      await fetchLaunch();
    } catch (err) {
      console.error('Failed to clear assets:', err);
      setError(err instanceof Error ? err.message : 'Failed to clear assets');
    } finally {
      setClearingAssets(false);
    }
  };

  // Fetch allowlist when Allowlist tab is active (phase-aware)
  const fetchAllowlist = useCallback(async (page = 1, search?: string, status?: AllowlistFilterStatus, isPhase2?: boolean) => {
    if (!id || !isAuthenticated) return;

    const usePhase2 = isPhase2 !== undefined ? isPhase2 : allowlistPhase2Selected;
    const fetchFn = usePhase2 ? getAllowlist2 : getAllowlist;

    try {
      setAllowlistLoading(true);
      const result = await fetchFn(id, {
        page,
        limit: 50,
        search: search ?? allowlistSearch,
        status: status ?? allowlistFilter,
      });
      setAllowlistEntries(result.entries);
      setAllowlistTotal(result.pagination.total);
      setAllowlistTotalPages(result.pagination.pages);
      setAllowlistStats(result.stats);
      setAllowlistPage(page);
    } catch (err) {
      console.error('Failed to load allowlist:', err);
    } finally {
      setAllowlistLoading(false);
    }
  }, [id, isAuthenticated, allowlistSearch, allowlistFilter, allowlistPhase2Selected]);

  useEffect(() => {
    if (activeTab === 'allowlist') {
      fetchAllowlist(1, allowlistSearch, allowlistFilter, allowlistPhase2Selected);
    }
  }, [activeTab]);

  // Re-fetch when phase selection changes
  useEffect(() => {
    if (activeTab === 'allowlist') {
      setSelectedAddresses(new Set()); // Clear selection when switching phases
      fetchAllowlist(1, allowlistSearch, allowlistFilter, allowlistPhase2Selected);
    }
  }, [allowlistPhase2Selected]);

  // Reset phase selection if phase 2 is no longer enabled
  useEffect(() => {
    if (launch && !launch.allowlist_2_enabled && allowlistPhase2Selected) {
      setAllowlistPhase2Selected(false);
    }
  }, [launch?.allowlist_2_enabled, allowlistPhase2Selected]);

  // Debounced search handler
  const handleAllowlistSearch = useCallback((value: string) => {
    setAllowlistSearch(value);
    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    // Debounce 300ms
    searchTimeoutRef.current = setTimeout(() => {
      setSelectedAddresses(new Set()); // Clear selection on search
      fetchAllowlist(1, value, allowlistFilter);
    }, 300);
  }, [fetchAllowlist, allowlistFilter]);

  // Handle filter change
  const handleAllowlistFilterChange = useCallback((newFilter: AllowlistFilterStatus) => {
    setAllowlistFilter(newFilter);
    setSelectedAddresses(new Set()); // Clear selection on filter change
    fetchAllowlist(1, allowlistSearch, newFilter);
  }, [fetchAllowlist, allowlistSearch]);

  // Handle page change
  const handleAllowlistPageChange = useCallback((newPage: number) => {
    setSelectedAddresses(new Set()); // Clear selection on page change
    fetchAllowlist(newPage, allowlistSearch, allowlistFilter);
  }, [fetchAllowlist, allowlistSearch, allowlistFilter]);

  // Fetch revenue data when Revenue tab is active
  const fetchRevenue = useCallback(async (page = 1) => {
    if (!id || !isAuthenticated) return;

    try {
      setRevenueLoading(true);
      const [revenueData, settlementsData] = await Promise.all([
        getLaunchRevenue(id),
        getLaunchSettlements(id, page, 10),
      ]);
      setRevenueStats(revenueData);
      setSettlements(settlementsData.settlements);
      setSettlementsPage(page);
      setSettlementsTotalPages(settlementsData.pagination.total_pages);
    } catch (err) {
      console.error('Failed to load revenue data:', err);
    } finally {
      setRevenueLoading(false);
    }
  }, [id, isAuthenticated]);

  useEffect(() => {
    if (activeTab === 'revenue') {
      fetchRevenue(1);
    }
  }, [activeTab, fetchRevenue]);

  // Fetch royalties data when Royalties tab is active
  const fetchRoyalties = useCallback(async () => {
    if (!id || !isAuthenticated) return;

    try {
      setRoyaltiesLoading(true);
      const data = await getLaunchRoyalties(id);
      setRoyaltyStats(data);
    } catch (err) {
      console.error('Failed to load royalties data:', err);
    } finally {
      setRoyaltiesLoading(false);
    }
  }, [id, isAuthenticated]);

  useEffect(() => {
    if (activeTab === 'royalties') {
      fetchRoyalties();
    }
  }, [activeTab, fetchRoyalties]);

  // Handle settlements CSV export
  const handleExportSettlementsCsv = async () => {
    if (!id) return;

    try {
      setExportingCsv(true);
      await exportLaunchSettlements(id);
    } catch (err) {
      console.error('Failed to export settlements:', err);
      setError(err instanceof Error ? err.message : 'Failed to export settlements');
    } finally {
      setExportingCsv(false);
    }
  };

  // XRPL address validation regex
  const XRPL_ADDRESS_REGEX = /^r[1-9A-HJ-NP-Za-km-z]{24,34}$/;

  // Add single entry to allowlist
  const handleAddEntry = async () => {
    if (!launch || !newAddress.trim()) return;

    const address = newAddress.trim();

    // Validate XRPL address format
    if (!XRPL_ADDRESS_REGEX.test(address)) {
      setError('Invalid XRP address. Addresses must start with "r" followed by 24-34 alphanumeric characters.');
      return;
    }

    try {
      setAddingEntry(true);
      const addFn = allowlistPhase2Selected ? addToAllowlist2 : addToAllowlist;
      await addFn(launch.id, [{
        address,
        max_mint: newMaxMint ? parseInt(newMaxMint, 10) : undefined,
      }]);
      setNewAddress('');
      setNewMaxMint('');
      await Promise.all([fetchAllowlist(1, allowlistSearch, allowlistFilter, allowlistPhase2Selected), fetchLaunch()]);
    } catch (err) {
      console.error('Failed to add entry:', err);
      setError(err instanceof Error ? err.message : 'Failed to add entry');
    } finally {
      setAddingEntry(false);
    }
  };

  // Handle CSV upload
  const handleCsvUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !launch) return;

    try {
      setCsvUploading(true);
      const text = await file.text();
      const lines = text.trim().split('\n');

      const entries: AddAllowlistEntry[] = [];

      for (let i = 0; i < lines.length; i++) {
        const line = lines[i].trim();
        if (!line) continue;

        // Skip header row if present
        if (i === 0 && (line.toLowerCase().includes('address') || line.toLowerCase().includes('wallet'))) {
          continue;
        }

        const parts = line.split(',').map(p => p.trim());
        const address = parts[0];
        const maxMintParsed = parts[1] ? parseInt(parts[1], 10) : undefined;

        if (address && /^r[1-9A-HJ-NP-Za-km-z]{24,34}$/.test(address)) {
          entries.push({ address, max_mint: isNaN(maxMintParsed as number) ? undefined : maxMintParsed });
        }
      }

      if (entries.length === 0) {
        setError('No valid addresses found in CSV');
        return;
      }

      const addFn = allowlistPhase2Selected ? addToAllowlist2 : addToAllowlist;
      await addFn(launch.id, entries);
      await Promise.all([fetchAllowlist(1, allowlistSearch, allowlistFilter, allowlistPhase2Selected), fetchLaunch()]);
    } catch (err) {
      console.error('Failed to upload CSV:', err);
      setError(err instanceof Error ? err.message : 'Failed to upload CSV');
    } finally {
      setCsvUploading(false);
      if (csvInputRef.current) {
        csvInputRef.current.value = '';
      }
    }
  };

  // Handle clear allowlist (called after confirmation)
  const handleClearAllowlist = async () => {
    if (!launch) return;

    try {
      setClearingAllowlist(true);
      setShowClearConfirm(false);
      const clearFn = allowlistPhase2Selected ? clearAllowlist2 : clearAllowlist;
      await clearFn(launch.id);
      setAllowlistEntries([]);
      setAllowlistTotal(0);
      await fetchLaunch();
    } catch (err) {
      console.error('Failed to clear allowlist:', err);
      setError(err instanceof Error ? err.message : 'Failed to clear allowlist');
    } finally {
      setClearingAllowlist(false);
    }
  };

  // Handle remove single entry from allowlist (called after confirmation)
  const handleRemoveEntry = async () => {
    if (!launch || !addressToRemove) return;

    try {
      setRemovingEntry(true);
      const removeFn = allowlistPhase2Selected ? removeFromAllowlist2 : removeFromAllowlist;
      await removeFn(launch.id, addressToRemove);
      // Remove from local state
      setAllowlistEntries(prev => prev.filter(e => e.wallet_address !== addressToRemove));
      setAllowlistTotal(prev => prev - 1);
      setAddressToRemove(null);
      await fetchLaunch();
    } catch (err) {
      console.error('Failed to remove entry:', err);
      setError(err instanceof Error ? err.message : 'Failed to remove entry');
    } finally {
      setRemovingEntry(false);
    }
  };

  // Export allowlist as CSV
  const handleExportCsv = () => {
    if (allowlistEntries.length === 0) return;

    const csv = ['address,max_mint,twitter_username,source'];
    for (const entry of allowlistEntries) {
      csv.push(`${entry.wallet_address},${entry.mint_limit ?? ''},${entry.registration_metadata?.twitter_username ?? ''},${entry.source ?? ''}`);
    }

    const phaseLabel = allowlistPhase2Selected ? 'phase2' : 'phase1';
    const blob = new Blob([csv.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `allowlist-${phaseLabel}-${launch?.slug || 'export'}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Toggle selection of an address
  const handleToggleSelection = (address: string) => {
    setSelectedAddresses((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(address)) {
        newSet.delete(address);
      } else {
        newSet.add(address);
      }
      return newSet;
    });
  };

  // Select/deselect all visible addresses
  const handleSelectAll = () => {
    if (selectedAddresses.size === allowlistEntries.length) {
      setSelectedAddresses(new Set());
    } else {
      setSelectedAddresses(new Set(allowlistEntries.map((e) => e.wallet_address)));
    }
  };

  // Clear selection
  const handleClearSelection = () => {
    setSelectedAddresses(new Set());
  };

  // Copy address to clipboard
  const handleCopyAddress = async (address: string) => {
    try {
      await navigator.clipboard.writeText(address);
      setCopiedAllowlistAddress(address);
      setTimeout(() => setCopiedAllowlistAddress(null), 2000);
    } catch (err) {
      console.error('Failed to copy address:', err);
    }
  };

  // Bulk delete selected addresses
  const handleBulkDelete = async () => {
    if (!launch || selectedAddresses.size === 0) return;

    try {
      setBulkDeleting(true);
      const addresses = Array.from(selectedAddresses);
      const bulkDeleteFn = allowlistPhase2Selected ? bulkDeleteFromAllowlist2 : bulkDeleteFromAllowlist;
      await bulkDeleteFn(launch.id, addresses);
      setSelectedAddresses(new Set());
      await Promise.all([fetchAllowlist(allowlistPage, allowlistSearch, allowlistFilter, allowlistPhase2Selected), fetchLaunch()]);
    } catch (err) {
      console.error('Failed to bulk delete:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete selected addresses');
    } finally {
      setBulkDeleting(false);
    }
  };

  // Bulk update mint limit
  const handleBulkUpdateLimit = async () => {
    if (!launch || selectedAddresses.size === 0 || !bulkMintLimit) return;

    const limit = parseInt(bulkMintLimit, 10);
    if (isNaN(limit) || limit < 0) {
      setError('Please enter a valid mint limit (0 or greater)');
      return;
    }

    try {
      setBulkUpdating(true);
      const addresses = Array.from(selectedAddresses);
      const bulkUpdateFn = allowlistPhase2Selected ? bulkUpdateAllowlist2Limit : bulkUpdateAllowlistLimit;
      await bulkUpdateFn(launch.id, addresses, limit);
      setSelectedAddresses(new Set());
      setShowBulkLimitModal(false);
      setBulkMintLimit('');
      await fetchAllowlist(allowlistPage, allowlistSearch, allowlistFilter, allowlistPhase2Selected);
    } catch (err) {
      console.error('Failed to bulk update limit:', err);
      setError(err instanceof Error ? err.message : 'Failed to update limits');
    } finally {
      setBulkUpdating(false);
    }
  };

  // Start inline editing
  const handleStartEdit = (entry: AllowlistEntry) => {
    setEditingAddress(entry.wallet_address);
    setEditingLimit(entry.mint_limit?.toString() || '0');
  };

  // Cancel inline editing
  const handleCancelEdit = () => {
    setEditingAddress(null);
    setEditingLimit('');
  };

  // Save inline edit
  const handleSaveEdit = async () => {
    if (!launch || !editingAddress) return;

    const limit = parseInt(editingLimit, 10);
    if (isNaN(limit) || limit < 0) {
      setError('Please enter a valid mint limit (0 or greater)');
      return;
    }

    try {
      setSavingEdit(true);
      const updateFn = allowlistPhase2Selected ? updateAllowlist2Entry : updateAllowlistEntry;
      await updateFn(launch.id, editingAddress, limit);
      // Update local state
      setAllowlistEntries((prev) =>
        prev.map((e) =>
          e.wallet_address === editingAddress ? { ...e, mint_limit: limit } : e
        )
      );
      setEditingAddress(null);
      setEditingLimit('');
    } catch (err) {
      console.error('Failed to save edit:', err);
      setError(err instanceof Error ? err.message : 'Failed to save changes');
    } finally {
      setSavingEdit(false);
    }
  };

  // Handle CSV upload with preview
  const handleCsvUploadWithPreview = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !launch) return;

    try {
      setCsvUploading(true);
      const text = await file.text();
      const lines = text.trim().split('\n');

      const entries: Array<{ wallet_address: string; mint_limit?: number }> = [];

      for (let i = 0; i < lines.length; i++) {
        const line = lines[i].trim();
        if (!line) continue;

        // Skip header row if present
        if (i === 0 && (line.toLowerCase().includes('address') || line.toLowerCase().includes('wallet'))) {
          continue;
        }

        const parts = line.split(',').map((p) => p.trim());
        const address = parts[0];
        const maxMintParsed = parts[1] ? parseInt(parts[1], 10) : undefined;

        if (address) {
          entries.push({
            wallet_address: address,
            mint_limit: isNaN(maxMintParsed as number) ? undefined : maxMintParsed,
          });
        }
      }

      if (entries.length === 0) {
        setError('No entries found in CSV');
        setCsvUploading(false);
        if (csvInputRef.current) {
          csvInputRef.current.value = '';
        }
        return;
      }

      // Get preview (phase-aware)
      const previewFn = allowlistPhase2Selected ? previewAllowlist2Import : previewAllowlistImport;
      const preview = await previewFn(launch.id, entries);
      setImportPreview(preview);
      setPendingImportEntries(entries);
      setShowImportPreview(true);
    } catch (err) {
      console.error('Failed to preview CSV:', err);
      setError(err instanceof Error ? err.message : 'Failed to preview CSV');
    } finally {
      setCsvUploading(false);
      if (csvInputRef.current) {
        csvInputRef.current.value = '';
      }
    }
  };

  // Confirm import after preview
  const handleConfirmImport = async () => {
    if (!launch || pendingImportEntries.length === 0) return;

    try {
      setCsvUploading(true);
      // Convert to AddAllowlistEntry format
      const entries: AddAllowlistEntry[] = pendingImportEntries
        .filter((e) => /^r[1-9A-HJ-NP-Za-km-z]{24,34}$/.test(e.wallet_address))
        .map((e) => ({
          address: e.wallet_address,
          max_mint: e.mint_limit,
        }));

      const addFn = allowlistPhase2Selected ? addToAllowlist2 : addToAllowlist;
      await addFn(launch.id, entries);
      setShowImportPreview(false);
      setImportPreview(null);
      setPendingImportEntries([]);
      await Promise.all([fetchAllowlist(1, allowlistSearch, allowlistFilter, allowlistPhase2Selected), fetchLaunch()]);
    } catch (err) {
      console.error('Failed to import:', err);
      setError(err instanceof Error ? err.message : 'Failed to import allowlist');
    } finally {
      setCsvUploading(false);
    }
  };

  // Cancel import preview
  const handleCancelImport = () => {
    setShowImportPreview(false);
    setImportPreview(null);
    setPendingImportEntries([]);
  };

  // Handle save
  const handleSave = async () => {
    if (!launch || Object.keys(editForm).length === 0) return;

    try {
      setSaving(true);
      setError(null);

      // Convert datetime-local values to ISO strings for proper timezone handling
      const formToSend = { ...editForm };
      if (formToSend.allowlist_start_at !== undefined) {
        formToSend.allowlist_start_at = localDateTimeToISO(formToSend.allowlist_start_at);
      }
      if (formToSend.allowlist_2_start_at !== undefined) {
        formToSend.allowlist_2_start_at = localDateTimeToISO(formToSend.allowlist_2_start_at);
      }
      if (formToSend.public_start_at !== undefined) {
        formToSend.public_start_at = localDateTimeToISO(formToSend.public_start_at);
      }
      if (formToSend.end_at !== undefined) {
        formToSend.end_at = localDateTimeToISO(formToSend.end_at);
      }

      const updated = await updateLaunch(launch.id, formToSend);
      setLaunch((prev) => (prev ? { ...prev, ...updated } : null));
      setIsEditing(false);
      setEditForm({});

      // If user was on allowlist tab and allowlist is now disabled, redirect to settings
      if (activeTab === 'allowlist' && updated.allowlist_enabled === false && updated.allowlist_2_enabled === false) {
        setActiveTab('settings');
      }
    } catch (err) {
      console.error('Failed to save:', err);
      setError(err instanceof Error ? err.message : 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  // Handle delete
  const handleDelete = async () => {
    if (!launch) return;

    try {
      setDeleting(true);
      await deleteLaunch(launch.id);
      navigate('/launchpad');
    } catch (err) {
      console.error('Failed to delete:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete launch');
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  // Handle submit for approval - initiates fee payment if not already paid
  const handleSubmit = async () => {
    if (!launch) return;

    try {
      setSubmitting(true);
      setPaymentError(null);

      // If fee is already paid, just submit directly
      if (launch.fee_paid) {
        await submitForApproval(launch.id);
        await fetchLaunch();
        setShowSubmitConfirm(false);
        return;
      }

      // Create fee payment payload
      const paymentData = await createFeePayment(launch.id);
      setFeePaymentPayload(paymentData);

      // Close confirmation modal and show payment modal
      setShowSubmitConfirm(false);
      setShowPaymentModal(true);
    } catch (err) {
      console.error('Failed to create payment:', err);
      setPaymentError(err instanceof Error ? err.message : 'Failed to create payment');
    } finally {
      setSubmitting(false);
    }
  };

  // Handle validation (Phase 10 Part 3)
  const handleValidate = async () => {
    if (!launch) return;

    try {
      setValidating(true);
      setValidationError(null);
      const result = await validateLaunch(launch.id);
      setValidationResult(result);
    } catch (err) {
      console.error('Failed to validate launch:', err);
      setValidationError(err instanceof Error ? err.message : 'Failed to validate launch');
    } finally {
      setValidating(false);
    }
  };

  // Handle go live (activate launch)
  const handleGoLive = async () => {
    if (!launch) return;

    try {
      setActivating(true);
      setError(null);

      await activateLaunch(launch.id);
      await fetchLaunch();
      setShowGoLiveConfirm(false);
    } catch (err) {
      console.error('Failed to activate launch:', err);
      setError(err instanceof Error ? err.message : 'Failed to go live');
    } finally {
      setActivating(false);
    }
  };

  // Handle pause launch
  const handlePause = async () => {
    if (!launch) return;

    try {
      setPausing(true);
      setError(null);
      await pauseLaunch(launch.id);
      await fetchLaunch();
    } catch (err) {
      console.error('Failed to pause launch:', err);
      setError(err instanceof Error ? err.message : 'Failed to pause launch');
    } finally {
      setPausing(false);
    }
  };

  // Handle resume launch
  const handleResume = async () => {
    if (!launch) return;

    try {
      setResuming(true);
      setError(null);
      await resumeLaunch(launch.id);
      await fetchLaunch();
    } catch (err) {
      console.error('Failed to resume launch:', err);
      setError(err instanceof Error ? err.message : 'Failed to resume launch');
    } finally {
      setResuming(false);
    }
  };

  // Handle advance to next phase
  const handleAdvancePhase = async () => {
    if (!launch) return;

    try {
      setAdvancingPhase(true);
      setError(null);
      setShowAdvancePhaseConfirm(false);
      const result = await advancePhase(launch.id);
      await fetchLaunch();
      // Optional: show success message
    } catch (err) {
      console.error('Failed to advance phase:', err);
      setError(err instanceof Error ? err.message : 'Failed to advance phase');
    } finally {
      setAdvancingPhase(false);
    }
  };

  // Get next phase info for display
  const getNextPhaseInfo = () => {
    if (!launch || launch.status !== 'active') return null;

    const currentPhase = launch.mint_phase;

    if (currentPhase === 'allowlist') {
      if (launch.allowlist_2_enabled) {
        return {
          nextPhase: 'allowlist_2' as const,
          nextPhaseName: 'Allowlist Phase 2',
          scheduledAt: launch.allowlist_2_start_at,
        };
      }
      return {
        nextPhase: 'public' as const,
        nextPhaseName: 'Public',
        scheduledAt: launch.public_start_at,
      };
    }

    if (currentPhase === 'allowlist_2') {
      return {
        nextPhase: 'public' as const,
        nextPhaseName: 'Public',
        scheduledAt: launch.public_start_at,
      };
    }

    // Already in public phase
    return null;
  };

  // Handle successful fee payment - hybrid verification approach
  // 1. Try backend verification endpoint (has its own retry logic)
  // 2. If that doesn't confirm, poll the launch status (ingestion service will detect on-chain)
  const handlePaymentSuccess = async (txHash: string) => {
    if (!launch || !feePaymentPayload) return;

    const launchId = launch.id;
    // The status endpoint is keyed by the signing id, which is the intent id for client-
    // signing wallets and the Xaman uuid otherwise — normalize to get the right one.
    const payloadUuid = toSigningPayload(feePaymentPayload.signingIntent ?? feePaymentPayload.payload).id;

    // Close the signing modal and show waiting state
    setShowPaymentModal(false);
    setWaitingForPayment(true);
    setPaymentError(null);

    // First, try the backend verification endpoint (quick path)
    try {
      const status = await checkFeePaymentStatus(launchId, payloadUuid);
      if (status.status === 'paid') {
        setWaitingForPayment(false);
        setFeePaymentPayload(null);
        await fetchLaunch();
        return;
      }
    } catch {
      // Backend verification didn't confirm yet - fall through to polling
    }

    // Start polling the launch status using setTimeout chain (prevents overlapping requests)
    // The ingestion service detects the payment on-chain and updates fee_paid
    const MAX_POLLS = 20; // 60 seconds total
    const POLL_INTERVAL_MS = 3000;

    // Clear any existing polling from a previous invocation
    if (paymentPollingRef.current) {
      clearTimeout(paymentPollingRef.current);
      paymentPollingRef.current = null;
    }

    const poll = async (count: number) => {
      if (count >= MAX_POLLS) {
        paymentPollingRef.current = null;
        setWaitingForPayment(false);
        setFeePaymentPayload(null);
        setPaymentError(
          'Payment was signed but on-chain confirmation is taking longer than expected. ' +
          'Your payment will be detected automatically. Please refresh the page in a few minutes.'
        );
        await fetchLaunch();
        return;
      }

      try {
        const data = await getLaunch(launchId);
        if (data.fee_paid) {
          paymentPollingRef.current = null;
          setWaitingForPayment(false);
          setFeePaymentPayload(null);
          setLaunch(data);
          return;
        }
      } catch {
        // Ignore fetch errors during polling
      }

      // Also try backend verification periodically (every 3rd poll)
      if (count % 3 === 0) {
        try {
          const status = await checkFeePaymentStatus(launchId, payloadUuid);
          if (status.status === 'paid') {
            paymentPollingRef.current = null;
            setWaitingForPayment(false);
            setFeePaymentPayload(null);
            await fetchLaunch();
            return;
          }
        } catch {
          // Ignore
        }
      }

      // Schedule next poll (setTimeout prevents overlapping if a request is slow)
      paymentPollingRef.current = setTimeout(() => poll(count + 1), POLL_INTERVAL_MS);
    };

    // Start first poll after delay
    paymentPollingRef.current = setTimeout(() => poll(0), POLL_INTERVAL_MS);
  };

  // Handle payment rejection
  const handlePaymentRejected = () => {
    setShowPaymentModal(false);
    setFeePaymentPayload(null);
    setPaymentError('Payment was rejected');
  };

  // Handle payment expiry
  const handlePaymentExpired = () => {
    setShowPaymentModal(false);
    setFeePaymentPayload(null);
    setPaymentError('Payment request expired');
  };

  // Handle payment modal close
  const handlePaymentModalClose = () => {
    setShowPaymentModal(false);
    setFeePaymentPayload(null);
    if (paymentPollingRef.current) {
      clearTimeout(paymentPollingRef.current);
      paymentPollingRef.current = null;
    }
    setWaitingForPayment(false);
  };

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (paymentPollingRef.current) {
        clearTimeout(paymentPollingRef.current);
      }
    };
  }, []);

  // Handle initiate authorization (AccountSet for NFTokenMinter)
  const handleAuthorize = async () => {
    if (!launch) return;

    try {
      setAuthorizingMinter(true);
      setAuthorizationError(null);

      // Create authorization payload
      const authData = await createAuthorizationRequest(launch.id);
      setAuthorizationPayload(authData);
      setShowAuthorizationModal(true);
    } catch (err) {
      console.error('Failed to create authorization request:', err);
      setAuthorizationError(err instanceof Error ? err.message : 'Failed to create authorization request');
    } finally {
      setAuthorizingMinter(false);
    }
  };

  // Handle successful authorization
  const handleAuthorizationSuccess = async (txHash: string) => {
    if (!launch || !authorizationPayload) return;

    try {
      // Verify authorization status with backend (keyed by signing id: intent id for
      // client-signing wallets, Xaman uuid otherwise).
      const signingId = toSigningPayload(authorizationPayload.signingIntent ?? authorizationPayload.payload).id;
      const status = await checkAuthorizationStatus(launch.id, signingId);

      if (status.status === 'authorized') {
        // Refresh launch data
        await fetchLaunch();
        setShowAuthorizationModal(false);
        setAuthorizationPayload(null);
      } else if (status.status === 'verification_failed') {
        // Authorization was signed but NFTokenMinter wasn't set correctly on XRPL
        // This usually means the SetFlag wasn't included in the transaction
        setShowAuthorizationModal(false);
        setAuthorizationPayload(null);
        setAuthorizationError(
          status.message || 'Authorization verification failed. The minter address was not set correctly on your account. Please try again.'
        );
      } else {
        // Fallback: Check directly on XRPL
        const xrplCheck = await checkAuthorizationOnXRPL(launch.id);
        if (xrplCheck.authorized) {
          await fetchLaunch();
          setShowAuthorizationModal(false);
          setAuthorizationPayload(null);
        }
      }
    } catch (err) {
      console.error('Failed to verify authorization:', err);
      // Still close modal and refresh - the backend will have updated the status
      await fetchLaunch();
      setShowAuthorizationModal(false);
      setAuthorizationPayload(null);
    }
  };

  // Handle authorization rejection
  const handleAuthorizationRejected = () => {
    setShowAuthorizationModal(false);
    setAuthorizationPayload(null);
    setAuthorizationError('Authorization was rejected. You can try again when ready.');
  };

  // Handle authorization expiry
  const handleAuthorizationExpired = () => {
    setShowAuthorizationModal(false);
    setAuthorizationPayload(null);
    setAuthorizationError('Authorization request expired. Please try again.');
  };

  // Handle authorization modal close
  const handleAuthorizationModalClose = () => {
    setShowAuthorizationModal(false);
    setAuthorizationPayload(null);
  };

  // Handle thumbnail upload
  const handleThumbnailUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !launch) return;

    try {
      setUploadingThumbnail(true);
      const result = await uploadLaunchThumbnail(launch.id, file);
      setLaunch((prev) =>
        prev ? { ...prev, thumbnail_url: result.thumbnail_url } : null
      );
    } catch (err) {
      console.error('Failed to upload thumbnail:', err);
      setError(err instanceof Error ? err.message : 'Failed to upload thumbnail');
    } finally {
      setUploadingThumbnail(false);
      if (thumbnailInputRef.current) {
        thumbnailInputRef.current.value = '';
      }
    }
  };

  // Handle banner upload
  const handleBannerUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !launch) return;

    try {
      setUploadingBanner(true);
      const result = await uploadLaunchBanner(launch.id, file);
      setLaunch((prev) =>
        prev ? { ...prev, banner_url: result.banner_url, banner_position_y: 50 } : null
      );
      setBannerPositionY(50);
    } catch (err) {
      console.error('Failed to upload banner:', err);
      setError(err instanceof Error ? err.message : 'Failed to upload banner');
    } finally {
      setUploadingBanner(false);
      if (bannerInputRef.current) {
        bannerInputRef.current.value = '';
      }
    }
  };

  // Handle banner remove
  const handleBannerRemove = async () => {
    if (!launch) return;
    try {
      setRemovingBanner(true);
      await deleteLaunchBanner(launch.id);
      setLaunch((prev) =>
        prev ? { ...prev, banner_url: null, banner_position_y: 50 } : null
      );
      setBannerPositionY(50);
    } catch (err) {
      console.error('Failed to remove banner:', err);
      setError(err instanceof Error ? err.message : 'Failed to remove banner');
    } finally {
      setRemovingBanner(false);
    }
  };

  // Save banner position (debounced)
  const saveBannerPosition = useCallback((posY: number) => {
    if (bannerSaveTimeoutRef.current) {
      clearTimeout(bannerSaveTimeoutRef.current);
    }
    bannerSaveTimeoutRef.current = setTimeout(async () => {
      if (!launch) return;
      try {
        await updateBannerPosition(launch.id, Math.round(posY));
        setLaunch((prev) =>
          prev ? { ...prev, banner_position_y: Math.round(posY) } : null
        );
      } catch (err) {
        console.error('Failed to save banner position:', err);
      }
    }, 300);
  }, [launch]);

  // Banner drag-to-reposition (mouse)
  const handleBannerMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    const startY = e.clientY;
    const startPos = bannerPositionY;

    const onMouseMove = (ev: MouseEvent) => {
      if (!bannerContainerRef.current) return;
      const containerHeight = bannerContainerRef.current.getBoundingClientRect().height;
      const deltaY = ev.clientY - startY;
      const deltaPercent = (deltaY / containerHeight) * -100;
      const newPosition = Math.min(100, Math.max(0, startPos + deltaPercent));
      setBannerPositionY(newPosition);
    };

    const onMouseUp = () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      setIsDraggingBanner(false);
      setBannerPositionY(prev => {
        saveBannerPosition(prev);
        return prev;
      });
    };

    setIsDraggingBanner(true);
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  }, [bannerPositionY, saveBannerPosition]);

  // Banner drag-to-reposition (touch)
  const handleBannerTouchStart = useCallback((e: React.TouchEvent) => {
    const touch = e.touches[0];
    const startY = touch.clientY;
    const startPos = bannerPositionY;

    const onTouchMove = (ev: TouchEvent) => {
      if (!bannerContainerRef.current) return;
      const t = ev.touches[0];
      const containerHeight = bannerContainerRef.current.getBoundingClientRect().height;
      const deltaY = t.clientY - startY;
      const deltaPercent = (deltaY / containerHeight) * -100;
      const newPosition = Math.min(100, Math.max(0, startPos + deltaPercent));
      setBannerPositionY(newPosition);
    };

    const onTouchEnd = () => {
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onTouchEnd);
      setIsDraggingBanner(false);
      setBannerPositionY(prev => {
        saveBannerPosition(prev);
        return prev;
      });
    };

    setIsDraggingBanner(true);
    window.addEventListener('touchmove', onTouchMove, { passive: true });
    window.addEventListener('touchend', onTouchEnd);
  }, [bannerPositionY, saveBannerPosition]);

  // Show login prompt if not authenticated
  if (!isAuthenticated) {
    return (
      <DefaultPageLayout>
        <div className="container max-w-4xl mx-auto px-4 py-12">
          <div className="flex flex-col items-center justify-center text-center gap-4">
            <h1 className="text-heading-2 font-heading-2 text-default-font">
              Collection Dashboard
            </h1>
            <p className="text-body font-body text-subtext-color max-w-md">
              Connect your wallet to view this collection.
            </p>
          </div>
        </div>
      </DefaultPageLayout>
    );
  }

  if (loading) {
    return (
      <DefaultPageLayout>
        <div className="flex items-center justify-center py-16">
          <LoadingSpinner size="md" />
        </div>
      </DefaultPageLayout>
    );
  }

  if (error && !launch) {
    return (
      <DefaultPageLayout>
        <div className="container max-w-4xl mx-auto px-4 py-12">
          <div className="flex flex-col items-center justify-center text-center gap-4">
            <p className="text-body font-body text-error-600">{error}</p>
            <Link to="/launchpad">
              <Button variant="neutral-secondary">Back to My Launches</Button>
            </Link>
          </div>
        </div>
      </DefaultPageLayout>
    );
  }

  if (!launch) {
    return (
      <DefaultPageLayout>
        <div className="container max-w-4xl mx-auto px-4 py-12">
          <div className="flex flex-col items-center justify-center text-center gap-4">
            <p className="text-body font-body text-subtext-color">Collection not found</p>
            <Link to="/launchpad">
              <Button variant="neutral-secondary">Back to My Launches</Button>
            </Link>
          </div>
        </div>
      </DefaultPageLayout>
    );
  }

  const isDraft = launch.status === 'draft';
  const canEditImages = launch.status !== 'completed';
  const statusDisplay = getStatusDisplay(launch.status);

  const getStatusBadgeClasses = (): string => {
    const baseClasses = 'px-3 py-1 rounded-full text-body font-body';
    switch (statusDisplay.color) {
      case 'green':
        return `${baseClasses} bg-success-100 text-success-700`;
      case 'yellow':
        return `${baseClasses} bg-warning-100 text-warning-700`;
      case 'blue':
        return `${baseClasses} bg-brand-100 text-brand-700`;
      case 'red':
        return `${baseClasses} bg-error-100 text-error-700`;
      default:
        return `${baseClasses} bg-neutral-100 text-neutral-700`;
    }
  };

  return (
    <DefaultPageLayout>
      <div className="container max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <Link
              to="/launchpad"
              className="flex items-center gap-2 text-caption font-caption text-subtext-color hover:text-default-font"
            >
              <FeatherArrowLeft className="w-4 h-4" />
              Back to My Launches
            </Link>
            <Link
              to="/launchpad/guide"
              className="text-caption font-caption text-brand-600 hover:underline"
            >
              Creator Guide
            </Link>
          </div>

          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-4">
              {/* Thumbnail */}
              <div className="relative w-20 h-20 bg-neutral-100 rounded-lg overflow-hidden group">
                {launch.thumbnail_url ? (
                  <img
                    src={launch.thumbnail_url}
                    alt={launch.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <span className="text-heading-2 font-heading-2 text-neutral-400">
                      {launch.name.charAt(0).toUpperCase()}
                    </span>
                  </div>
                )}
                {canEditImages && (
                  <button
                    onClick={() => thumbnailInputRef.current?.click()}
                    disabled={uploadingThumbnail}
                    className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                  >
                    {uploadingThumbnail ? (
                      <LoadingSpinner size="sm" />
                    ) : (
                      <FeatherUpload className="w-6 h-6 text-white" />
                    )}
                  </button>
                )}
                <input
                  ref={thumbnailInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  className="hidden"
                  onChange={handleThumbnailUpload}
                />
              </div>

              <div>
                {launch.project && (
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-body font-body text-subtext-color">Project:</span>
                    <Link
                      to={`/creator/projects/${launch.project.slug}`}
                      className="text-body font-body text-brand-700 hover:text-brand-600 underline transition-colors"
                    >
                      {launch.project.name}
                    </Link>
                  </div>
                )}
                <div className="flex items-center gap-3">
                  <h1 className="text-heading-1 font-heading-1 text-default-font">
                    {launch.name}
                  </h1>
                  <span className={getStatusBadgeClasses()}>{statusDisplay.label}</span>
                  <span className="px-3 py-1 rounded-full text-body font-body bg-neutral-100 text-neutral-700">
                    {launch.collection_type === 'composable'
                      ? 'Build Your Own'
                      : launch.collection_type === 'edition'
                        ? 'Editions'
                        : 'Standard'}
                  </span>
                </div>
                <p className="text-body font-body text-subtext-color mt-1">
                  /{launch.slug}
                </p>
                <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-caption font-caption text-subtext-color">
                  <span>
                    <span className="text-neutral-400">Issuer:</span>{' '}
                    <span className="font-mono">{launch.creator_address}</span>
                  </span>
                  <span>
                    <span className="text-neutral-400">Taxon:</span>{' '}
                    {launch.taxon}
                  </span>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {isDraft && (
                <>
                  <Button
                    variant="neutral-secondary"
                    icon={<FeatherTrash2 />}
                    onClick={() => setShowDeleteConfirm(true)}
                  >
                    Delete
                  </Button>
                  <Button
                    icon={<FeatherSend />}
                    onClick={() => setShowSubmitConfirm(true)}
                    disabled={launch.collection_type === 'composable'
                      ? !validationResult?.readyForSubmission
                      : launch.total_items === 0}
                  >
                    Submit for Approval
                  </Button>
                </>
              )}
              {launch.status === 'active' && (
                <Button
                  variant="neutral-secondary"
                  icon={<FeatherPause />}
                  onClick={handlePause}
                  loading={pausing}
                >
                  Pause Minting
                </Button>
              )}
              {launch.status === 'paused' && (
                <Button
                  icon={<FeatherPlay />}
                  onClick={handleResume}
                  loading={resuming}
                >
                  Resume Minting
                </Button>
              )}
              {['active', 'paused', 'completed'].includes(launch.status) && launch.collection_id && (
                <Button
                  variant="neutral-secondary"
                  icon={<FeatherExternalLink />}
                  onClick={() => navigate(`/collections/${launch.slug}`)}
                >
                  View Collection
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="mb-6 p-4 bg-error-100 border border-error-200 rounded-lg">
            <p className="text-body font-body text-error-700">{error}</p>
          </div>
        )}

        {/* Rejection Reason Banner */}
        {launch.admin_rejection_reason && launch.status === 'draft' && (
          <div className="mb-6 p-4 bg-yellow-50 border border-yellow-300 rounded-lg">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 mt-0.5">
                <svg className="w-5 h-5 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-yellow-800">
                  Your launch was not approved
                </h3>
                <p className="mt-1 text-sm text-yellow-700">
                  {launch.admin_rejection_reason}
                </p>
                <p className="mt-2 text-xs text-yellow-600">
                  Please address the feedback above and submit again when ready.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Existing Minter Warning Banner */}
        {launch.status === 'pending_authorization' && existingMinterAddress && (() => {
          const KNOWN_MINTER_NAMES: Record<string, string> = {
            'rKqqb5QZXVAL3VqXJL6obfRGeHou1DtyBV': 'xrp.cafe',
          };
          const knownName = KNOWN_MINTER_NAMES[existingMinterAddress];
          const minterDisplay = knownName || `${existingMinterAddress.slice(0, 8)}...${existingMinterAddress.slice(-6)}`;
          return (
            <div className="mb-6 p-4 bg-blue-50 border border-blue-300 rounded-lg">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 mt-0.5">
                  <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-blue-800">
                    Existing Minter Authorization Detected
                  </h3>
                  <p className="mt-1 text-sm text-blue-700">
                    Your wallet currently has <strong>{minterDisplay}</strong> set as its authorized minter.
                    XRPL only allows one authorized minter per account at a time &mdash; when you authorize xrp.deals, this will replace the existing authorization.
                  </p>
                  <p className="mt-2 text-sm text-blue-700">
                    If you have a collection currently being minted on {knownName || 'the other platform'}, minting there will stop working once you authorize xrp.deals.
                    If your other collection has already finished minting, this won&apos;t cause any issues.
                  </p>
                  <p className="mt-2 text-xs text-blue-600">
                    Recommendation: If you have a collection still minting, either wait until it finishes or use a different issuing wallet for this launch.
                  </p>
                </div>
              </div>
            </div>
          );
        })()}

        {/* Authorization Required Banner */}
        {launch.status === 'pending_authorization' && (
          <div className="mb-6 p-6 rounded-lg border border-brand-500 bg-default-background">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 mt-0.5">
                <svg className="w-8 h-8 text-brand-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-body-bold font-body-bold text-default-font">
                  Authorization Required
                </h3>
                <p className="mt-2 text-body font-body text-default-font">
                  Your collection has been approved! To proceed, you need to authorize our minting wallet to mint NFTs on your behalf.
                  This is a one-time setup using an XRPL AccountSet transaction.
                </p>
                <p className="mt-2 text-body font-body text-subtext-color">
                  This allows you to retain ownership as the issuer while we handle the minting process.
                  You can revoke this authorization at any time after your launch is complete.
                </p>
                {authorizationError && (
                  <p className="mt-3 text-body font-body text-error-500">
                    {authorizationError}
                  </p>
                )}
                <div className="mt-4">
                  <Button
                    onClick={handleAuthorize}
                    loading={authorizingMinter}
                    disabled={authorizingMinter}
                  >
                    Authorize Minter Wallet
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Re-Authorization Required Banner (paused due to revoked minter auth) */}
        {launch.status === 'paused' && !launch.minter_authorized && (
          <div className="mb-6 p-6 rounded-lg border border-brand-500 bg-default-background">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 mt-0.5">
                <svg className="w-8 h-8 text-brand-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-body-bold font-body-bold text-default-font">
                  Re-Authorization Required
                </h3>
                <p className="mt-2 text-body font-body text-default-font">
                  Minting has been paused because your account no longer has our minting wallet authorized.
                  This can happen if the NFTokenMinter field on your account was changed or cleared.
                </p>
                <p className="mt-2 text-body font-body text-subtext-color">
                  To resume minting, please re-authorize our minting wallet below.
                  Your launch will automatically resume once authorization is confirmed.
                </p>
                {authorizationError && (
                  <p className="mt-3 text-body font-body text-error-500">
                    {authorizationError}
                  </p>
                )}
                <div className="mt-4">
                  <Button
                    onClick={handleAuthorize}
                    loading={authorizingMinter}
                    disabled={authorizingMinter}
                  >
                    Re-Authorize Minter Wallet
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Auto-Pause Warning Banner (generic errors, not auth revocation) */}
        {launch.status === 'paused' && launch.auto_paused_at && launch.minter_authorized && (
          <div className="mb-6 p-4 rounded-lg border border-red-300 bg-red-50">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 mt-0.5">
                <svg className="w-5 h-5 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div>
                <h3 className="font-semibold text-red-800">Launch Auto-Paused</h3>
                <p className="mt-1 text-sm text-red-700">
                  {launch.auto_pause_reason || 'Minting was automatically paused due to consecutive errors.'}
                </p>
                <p className="mt-2 text-xs text-red-600">
                  Paused at: {new Date(launch.auto_paused_at).toLocaleString()}
                </p>
                <p className="mt-2 text-sm text-red-700">
                  Please contact support to investigate and resume your launch.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* IPFS Processing Progress */}
        {launch.ipfs_progress && (() => {
          const pct = Math.round((launch.ipfs_progress!.completed / launch.ipfs_progress!.total) * 100);
          return (
            <div className="mb-6 p-4 rounded-lg border border-neutral-border bg-default-background">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 mt-0.5">
                  <LoadingSpinner size="sm" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <h3 className="text-body-bold font-body-bold text-default-font">
                      Preparing NFT Metadata
                    </h3>
                    <span className="text-body font-body text-default-font">
                      {pct}%
                    </span>
                  </div>
                  <div className="w-full bg-neutral-200 dark:bg-neutral-700 rounded-full h-1.5 mt-2">
                    <div
                      className="bg-brand-500 h-1.5 rounded-full transition-all"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <p className="text-body font-body text-subtext-color mt-2">
                    {launch.ipfs_progress!.completed.toLocaleString()} of {launch.ipfs_progress!.total.toLocaleString()}{' '}
                    {launch.collection_type === 'edition' ? 'copies' : 'items'} pinned to IPFS.
                    {launch.collection_type === 'edition'
                      ? ' Each copy gets its own numbered metadata; identical artwork is only uploaded once.'
                      : ''}
                    {' '}The collection cannot go live until this finishes.
                  </p>
                </div>
              </div>
            </div>
          );
        })()}

        {/* Banner */}
        <div
          ref={bannerContainerRef}
          className="relative h-48 bg-neutral-100 rounded-lg overflow-hidden mb-8 group"
        >
          {launch.banner_url ? (
            <>
              <img
                src={launch.banner_url}
                alt="Banner"
                className="w-full h-full object-cover select-none"
                style={{ objectPosition: `center ${bannerPositionY}%` }}
                draggable={false}
                {...(canEditImages ? {
                  onMouseDown: handleBannerMouseDown,
                  onTouchStart: handleBannerTouchStart,
                } : {})}
              />
              {canEditImages && !isDraggingBanner && (
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-3 pointer-events-none">
                  <span className="bg-black/60 text-white text-caption font-caption px-3 py-1.5 rounded-full">
                    Drag to reposition
                  </span>
                </div>
              )}
              {isDraggingBanner && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <span className="bg-black/60 text-white text-caption font-caption px-3 py-1.5 rounded-full">
                    Release to save
                  </span>
                </div>
              )}
              {canEditImages && !isDraggingBanner && (
                <div className="absolute top-2 right-2 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={(e) => { e.stopPropagation(); bannerInputRef.current?.click(); }}
                    disabled={uploadingBanner || removingBanner}
                    className="bg-black/60 hover:bg-black/80 text-white p-2 rounded-lg transition-colors"
                    title="Replace Banner"
                  >
                    {uploadingBanner ? <LoadingSpinner size="sm" /> : <FeatherUpload className="w-4 h-4" />}
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); handleBannerRemove(); }}
                    disabled={uploadingBanner || removingBanner}
                    className="bg-black/60 hover:bg-red-600/80 text-white p-2 rounded-lg transition-colors"
                    title="Remove Banner"
                  >
                    {removingBanner ? <LoadingSpinner size="sm" /> : <FeatherTrash2 className="w-4 h-4" />}
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              {canEditImages ? (
                <button
                  onClick={() => bannerInputRef.current?.click()}
                  disabled={uploadingBanner}
                  className="flex items-center gap-2 text-subtext-color hover:text-default-font transition-colors"
                >
                  {uploadingBanner ? (
                    <LoadingSpinner size="sm" />
                  ) : (
                    <>
                      <FeatherUpload className="w-6 h-6" />
                      <span className="text-body font-body">Upload Banner</span>
                    </>
                  )}
                </button>
              ) : (
                <FeatherImage className="w-12 h-12 text-neutral-300" />
              )}
            </div>
          )}
          <input
            ref={bannerInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            className="hidden"
            onChange={handleBannerUpload}
          />
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-4 border-b border-neutral-200 mb-6">
          {(['overview', 'settings', 'traits', 'editions', 'assets', 'allowlist', 'mint-contest', 'referrals', 'ambassadors', 'revenue', 'royalties'] as TabId[])
            .filter((tab) => tab !== 'traits' || launch.collection_type === 'composable')
            .filter((tab) => tab !== 'editions' || launch.collection_type === 'edition')
            // Standard 'assets' uploader is only for standard launches
            .filter((tab) => tab !== 'assets' || launch.collection_type === 'standard')
            .filter((tab) => tab !== 'allowlist' || launch.allowlist_enabled || launch.allowlist_2_enabled)
            .filter((tab) => tab !== 'ambassadors' || (!['cancelled'].includes(launch.status) && (!launch.project || launch.project.enabled_features?.includes('ambassador_program'))))
            .filter((tab) => tab !== 'referrals' || !['cancelled'].includes(launch.status))
            .filter((tab) => tab !== 'mint-contest' || !['cancelled'].includes(launch.status))
            .filter((tab) => tab !== 'revenue' || ['active', 'paused', 'completed'].includes(launch.status))
            .filter((tab) => tab !== 'royalties' || (
              ['active', 'paused', 'completed'].includes(launch.status) &&
              (launch.transfer_fee || 0) > 0
            ))
            .map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-4 py-3 text-body font-body border-b-2 -mb-px transition-colors ${
                  activeTab === tab
                    ? 'border-brand-500 text-brand-600'
                    : 'border-transparent text-subtext-color hover:text-default-font'
                } ${tab === 'revenue' || tab === 'royalties' ? 'flex items-center gap-2' : ''}`}
              >
                {tab === 'revenue' && <FeatherDollarSign className="w-4 h-4" />}
                {tab === 'royalties' && <FeatherDollarSign className="w-4 h-4" />}
                {tab === 'mint-contest' ? 'Mint Contest' : tab === 'ambassadors' ? 'Promote' : tab === 'referrals' ? 'Referrals' : tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          {/* Schedule Launch tab - only shown for ready launches, Schedule tab for active/paused */}
          {launch.status === 'ready' && (
            <button
              onClick={() => setActiveTab('schedule')}
              className={`px-4 py-2 text-body font-body-bold transition-colors flex items-center gap-2 rounded-full ${
                activeTab === 'schedule'
                  ? 'bg-success-600 text-white'
                  : 'bg-success-500 text-white hover:bg-success-600'
              }`}
            >
              <FeatherClock className="w-4 h-4" />
              <span>Schedule Launch</span>
            </button>
          )}
          {['active', 'paused'].includes(launch.status) && (
            <button
              onClick={() => setActiveTab('schedule')}
              className={`px-4 py-3 text-body font-body border-b-2 -mb-px transition-colors flex items-center gap-2 ${
                activeTab === 'schedule'
                  ? 'border-brand-500 text-brand-600'
                  : 'border-transparent text-subtext-color hover:text-default-font'
              }`}
            >
              <FeatherClock className="w-4 h-4" />
              <span>Schedule</span>
            </button>
          )}
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Pre-Launch Checklist - shown for draft and pending_approval launches */}
            {['draft', 'pending_approval'].includes(launch.status) && (
              <div className="p-6 rounded-lg border border-neutral-border bg-default-background">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-body-bold font-body-bold text-default-font">
                      Pre-Launch Checklist
                    </h3>
                    <p className="text-caption font-caption text-subtext-color mt-1">
                      Verify your launch is ready before submitting for approval
                    </p>
                  </div>
                  <Button
                    variant="brand-secondary"
                    size="medium"
                    onClick={handleValidate}
                    loading={validating}
                    disabled={validating}
                  >
                    {validating ? 'Validating...' : validationResult ? 'Re-validate' : 'Run Validation'}
                  </Button>
                </div>

                {validationError && (
                  <div className="mb-4 p-3 rounded-lg bg-error-50 dark:bg-error-900/20 border border-error-200 dark:border-error-800">
                    <p className="text-body font-body text-error-600 dark:text-error-400">
                      {validationError}
                    </p>
                  </div>
                )}

                {validationResult ? (
                  <div className="space-y-4">
                    {/* Summary - clickable to expand/collapse */}
                    <button
                      type="button"
                      onClick={() => setChecklistExpanded(!checklistExpanded)}
                      className="w-full flex items-center justify-between p-4 rounded-lg border border-neutral-border bg-neutral-100 hover:bg-neutral-200 transition-colors cursor-pointer"
                    >
                      <div className="flex items-center gap-6">
                        <div className="flex items-center gap-2">
                          <span className="inline-flex items-center justify-center w-7 h-7 rounded-full text-body-bold font-body-bold bg-success-600 text-white">
                            {validationResult.summary.passed}
                          </span>
                          <span className="text-body font-body text-default-font">passed</span>
                        </div>
                        {validationResult.summary.warnings > 0 && (
                          <div className="flex items-center gap-2">
                            <span className="inline-flex items-center justify-center w-7 h-7 rounded-full text-body-bold font-body-bold bg-warning-500 text-white">
                              {validationResult.summary.warnings}
                            </span>
                            <span className="text-body font-body text-default-font">warnings</span>
                          </div>
                        )}
                        {validationResult.summary.failures > 0 && (
                          <div className="flex items-center gap-2">
                            <span className="inline-flex items-center justify-center w-7 h-7 rounded-full text-body-bold font-body-bold bg-error-600 text-white">
                              {validationResult.summary.failures}
                            </span>
                            <span className="text-body font-body text-default-font">failures</span>
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-3">
                        {validationResult.valid ? (
                          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-body-bold font-body-bold bg-success-600 text-white">
                            <FeatherCheck className="w-4 h-4" />
                            Ready for submission
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-body-bold font-body-bold bg-error-600 text-white">
                            <FeatherAlertTriangle className="w-4 h-4" />
                            Fix issues before submitting
                          </span>
                        )}
                        <FeatherChevronDown className={`w-5 h-5 text-subtext-color transition-transform ${checklistExpanded ? 'rotate-180' : ''}`} />
                      </div>
                    </button>

                    {/* Checks List - collapsible */}
                    {checklistExpanded && (
                    <div className="space-y-2">
                      {validationResult.checks.map((check) => {
                        return (
                          <div
                            key={check.id}
                            className="w-full text-left flex items-start gap-4 p-4 rounded-lg border border-neutral-border"
                          >
                            <span className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center ${
                              check.status === 'pass'
                                ? 'bg-success-600'
                                : check.status === 'warn'
                                ? 'bg-warning-500'
                                : 'bg-error-600'
                            }`}>
                              {check.status === 'pass' && <FeatherCheck className="w-4 h-4 text-white" />}
                              {check.status === 'warn' && <FeatherAlertTriangle className="w-4 h-4 text-white" />}
                              {check.status === 'fail' && <FeatherX className="w-4 h-4 text-white" />}
                            </span>
                            <div className="flex-1 min-w-0">
                              <span className="text-body-bold font-body-bold text-default-font">
                                {check.name}
                              </span>
                              <p className="text-body font-body text-subtext-color mt-1">
                                {check.message}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    )}
                  </div>
                ) : null}
              </div>
            )}

            {/* Existing Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Stats Card */}
            <div className="p-6 rounded-lg border border-neutral-border bg-default-background">
              <h3 className="text-body-bold font-body-bold text-default-font mb-4">
                Statistics
              </h3>
              <div className="space-y-3">
                {launch.collection_type === 'composable' && launch.total_items > 0 && (
                  <div className="flex justify-between items-center pb-3 mb-3 border-b border-neutral-border">
                    <span className="text-body-bold font-body-bold text-default-font">
                      Total Mint Supply
                    </span>
                    <span className="text-heading-3 font-heading-3 text-brand-primary">
                      {launch.total_items.toLocaleString()}
                    </span>
                  </div>
                )}
                {launch.collection_type !== 'composable' && (
                  <div className="flex justify-between">
                    <span className="text-body font-body text-subtext-color">
                      Total Items
                    </span>
                    <span className="text-body font-body text-default-font">
                      {launch.total_items}
                    </span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-body font-body text-subtext-color">
                    Minted
                  </span>
                  <span className="text-body font-body text-default-font">
                    {launch.minted_count}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-body font-body text-subtext-color">
                    Mint Price
                  </span>
                  <span className="text-body font-body text-default-font">
                    {formatMintPrice(launch.mint_price_drops)}
                  </span>
                </div>
                {launch.calculated_fee && (
                  <div className="flex justify-between">
                    <span className="text-body font-body text-subtext-color">
                      Platform Fee
                    </span>
                    <span className="text-body font-body text-default-font">
                      {launch.calculated_fee.feeXrp.toFixed(2)} XRP
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Details Card */}
            <div className="p-6 rounded-lg border border-neutral-border bg-default-background">
              <h3 className="text-body-bold font-body-bold text-default-font mb-4">
                Details
              </h3>
              <div className="space-y-3">
                <div>
                  <span className="text-caption font-caption text-subtext-color block mb-1">
                    Description
                  </span>
                  <p className="text-body font-body text-default-font">
                    {launch.description || 'No description'}
                  </p>
                </div>
                {launch.website_url && (
                  <div>
                    <span className="text-caption font-caption text-subtext-color block mb-1">
                      Website
                    </span>
                    <a
                      href={launch.website_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-body font-body text-brand-600 hover:underline"
                    >
                      {launch.website_url}
                    </a>
                  </div>
                )}
                {launch.twitter_handle && (
                  <div>
                    <span className="text-caption font-caption text-subtext-color block mb-1">
                      Twitter
                    </span>
                    <a
                      href={`https://x.com/${launch.twitter_handle.replace('@', '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-body font-body text-brand-600 hover:underline"
                    >
                      {launch.twitter_handle}
                    </a>
                  </div>
                )}
                {launch.discord_url && (
                  <div>
                    <span className="text-caption font-caption text-subtext-color block mb-1">
                      Discord
                    </span>
                    <a
                      href={launch.discord_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-body font-body text-brand-600 hover:underline"
                    >
                      {launch.discord_url}
                    </a>
                  </div>
                )}
              </div>
            </div>

            {/* Item Status Card */}
            <div className="p-6 rounded-lg border border-neutral-border bg-default-background">
              <h3 className="text-body-bold font-body-bold text-default-font mb-4">
                Item Status
              </h3>
              <div className="space-y-2">
                {Object.entries(launch.item_counts).map(([status, count]) => (
                  <div key={status} className="flex justify-between items-center">
                    <span className="text-body font-body text-subtext-color capitalize">
                      {status === 'pending' ? 'Available' : status}
                    </span>
                    <span className="text-body font-body text-default-font">
                      {count}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Allowlist Card - only shown when allowlist is enabled */}
            {(launch.allowlist_enabled || launch.allowlist_2_enabled) && (
              <div className="p-6 rounded-lg border border-neutral-border bg-default-background">
                <h3 className="text-body-bold font-body-bold text-default-font mb-4">
                  Allowlist
                </h3>
                <div className="space-y-3">
                  {launch.allowlist_enabled && (
                    <div className="flex justify-between">
                      <span className="text-body font-body text-subtext-color">
                        Phase 1 Addresses
                      </span>
                      <span className="text-body font-body text-default-font">
                        {launch.allowlist_count ?? 0}
                      </span>
                    </div>
                  )}
                  {launch.allowlist_2_enabled && (
                    <div className="flex justify-between">
                      <span className="text-body font-body text-subtext-color">
                        Phase 2 Addresses
                      </span>
                      <span className="text-body font-body text-default-font">
                        {launch.allowlist_2_count ?? 0}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Build Your Own Stats Card - only shown for composable collections */}
            {launch.collection_type === 'composable' && (
              <div className="p-6 rounded-lg border border-warning-200 bg-warning-50">
                <h3 className="text-body-bold font-body-bold text-default-font mb-4 flex items-center gap-2">
                  <FeatherZap className="w-4 h-4 text-warning-600" />
                  Build Your Own Activity
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-body font-body text-subtext-color">
                      Build Fee
                    </span>
                    <span className="text-body font-body text-default-font">
                      {(launch.composition_fee_per_nft_drops || 0) / 1_000_000} XRP
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-body font-body text-subtext-color">
                      Total Built
                    </span>
                    <span className="text-body font-body text-default-font">
                      {launch.total_composed_count || 0}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-body font-body text-subtext-color">
                      Total Burned
                    </span>
                    <span className="text-body font-body text-default-font">
                      {launch.total_burned_count || 0}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-body font-body text-subtext-color">
                      Build Revenue
                    </span>
                    <span className="text-body font-body text-default-font">
                      {((launch.compose_revenue_drops || 0) / 1_000_000).toFixed(2)} XRP
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-body font-body text-subtext-color">
                      Build Status
                    </span>
                    <span className={`text-body font-body ${launch.compose_enabled ? 'text-success-600' : 'text-subtext-color'}`}>
                      {launch.compose_enabled ? 'Enabled' : 'Disabled'}
                    </span>
                  </div>
                </div>
              </div>
            )}
            </div>
          </div>
        )}

        {activeTab === 'settings' && (
          <div className="max-w-xl">
            {['draft', 'pending_approval', 'pending_authorization', 'ready', 'active', 'paused'].includes(launch.status) ? (
              <div className="space-y-6">
                {/* Global NFT Settings */}
                <div className="pb-6 border-b border-neutral-border">
                  <h4 className="text-body-bold font-body-bold text-default-font mb-1">
                    NFT Settings
                  </h4>
                  <p className="text-caption font-caption text-subtext-color mb-4">
                    These settings apply to all NFTs in this collection.
                  </p>

                  <TextField
                    label={
                      <span className="flex items-center gap-2">
                        Royalty (%)
                        {launch.status !== 'draft' && (
                          <span className="text-caption text-warning-600">(Locked)</span>
                        )}
                      </span>
                    }
                    helpText="Secondary sales royalty (0-50%). Examples: 3 for 3%, 2.5 for 2.5%"
                  >
                    <TextField.Input
                      type="number"
                      min="0"
                      max="50"
                      step="0.1"
                      placeholder="0"
                      disabled={launch.status !== 'draft'}
                      value={String(
                        editForm.transfer_fee !== undefined
                          ? editForm.transfer_fee / 1000
                          : (launch.transfer_fee ?? 0) / 1000
                      )}
                      onChange={(e) =>
                        setEditForm((prev) => ({
                          ...prev,
                          transfer_fee: Math.round(parseFloat(e.target.value || '0') * 1000),
                        }))
                      }
                      onWheel={(e) => e.currentTarget.blur()}
                    />
                  </TextField>

                  {/* Burnable by Issuer Toggle */}
                  <div className="flex items-start gap-3 pt-2">
                    <input
                      type="checkbox"
                      id="burnable-nfts"
                      className="mt-1 h-4 w-4 rounded border-neutral-border accent-brand-500"
                      disabled={launch.status !== 'draft'}
                      checked={
                        editForm.nft_flags !== undefined
                          ? (editForm.nft_flags & 1) !== 0
                          : ((launch.nft_flags ?? 8) & 1) !== 0
                      }
                      onChange={(e) => {
                        const currentFlags = editForm.nft_flags ?? launch.nft_flags ?? 8;
                        const newFlags = e.target.checked
                          ? currentFlags | 1   // Set tfBurnable bit
                          : currentFlags & ~1; // Clear tfBurnable bit
                        setEditForm((prev) => ({ ...prev, nft_flags: newFlags }));
                      }}
                    />
                    <label htmlFor="burnable-nfts" className="flex flex-col gap-0.5 cursor-pointer">
                      <span className="text-body font-body-bold text-default-font">
                        Burnable by Issuer
                        {launch.status !== 'draft' && (
                          <span className="text-caption text-warning-600 ml-2">(Locked)</span>
                        )}
                      </span>
                      <span className="text-caption font-caption text-subtext-color">
                        Allow the issuer to burn NFTs even after they've been transferred to holders. Holders can always burn their own NFTs regardless of this setting.
                      </span>
                    </label>
                  </div>

                  {/* Mutable NFTs Toggle */}
                  <div className="flex items-start gap-3 pt-2">
                    <input
                      type="checkbox"
                      id="mutable-nfts"
                      className="mt-1 h-4 w-4 rounded border-neutral-border accent-brand-500"
                      disabled={launch.status !== 'draft'}
                      checked={
                        editForm.nft_flags !== undefined
                          ? (editForm.nft_flags & 16) !== 0
                          : ((launch.nft_flags ?? 8) & 16) !== 0
                      }
                      onChange={(e) => {
                        const currentFlags = editForm.nft_flags ?? launch.nft_flags ?? 8;
                        const newFlags = e.target.checked
                          ? currentFlags | 16   // Set tfMutable bit
                          : currentFlags & ~16; // Clear tfMutable bit
                        setEditForm((prev) => ({ ...prev, nft_flags: newFlags }));
                      }}
                    />
                    <label htmlFor="mutable-nfts" className="flex flex-col gap-0.5 cursor-pointer">
                      <span className="text-body font-body-bold text-default-font">
                        Mutable NFTs
                        {launch.status !== 'draft' && (
                          <span className="text-caption text-warning-600 ml-2">(Locked)</span>
                        )}
                      </span>
                      <span className="text-caption font-caption text-subtext-color">
                        Allow NFT metadata to be updated after minting. This flag is permanent and cannot be changed after minting begins.
                      </span>
                    </label>
                  </div>

                  {/* Composition Fee - only for composable collections */}
                  {launch.collection_type === 'composable' && (
                    <div className="pt-4">
                      <TextField
                        label="Build Your Own Fee (XRP)"
                        helpText="One-time fee for each build, regardless of how many traits are combined. Set to 0 for free builds."
                      >
                        <TextField.Input
                          type="number"
                          min="0"
                          step="0.1"
                          placeholder="0"
                          value={String(
                            editForm.composition_fee_per_nft_drops !== undefined
                              ? dropsToXrpTenths(editForm.composition_fee_per_nft_drops)
                              : dropsToXrpTenths(launch.composition_fee_per_nft_drops)
                          )}
                          onChange={(e) =>
                            setEditForm((prev) => ({
                              ...prev,
                              composition_fee_per_nft_drops:
                                e.target.value === ''
                                  ? 0
                                  : xrpTenthsToDrops(parseFloat(e.target.value) || 0),
                            }))
                          }
                        />
                      </TextField>
                    </div>
                  )}
                </div>

                {/* Mint Phases Header */}
                <div>
                  <h3 className="text-heading-3 font-heading-3 text-default-font">
                    Mint Phases
                  </h3>
                </div>

                {/* Warning banner when launch is active */}
                {(launch.status === 'active' || launch.status === 'paused') && launch.mint_phase && (
                  <div className="p-3 rounded-lg border border-amber-500/50 bg-amber-500/10">
                    <p className="text-caption font-caption text-amber-200">
                      ⚠️ Prices are locked once their phase begins and cannot be changed. Phase toggles are also locked once the launch is active.
                    </p>
                  </div>
                )}

                {/* Timeline Container */}
                <div className="relative pb-2">
                  {/* Vertical Timeline Line */}
                  <div className="absolute left-4 top-10 bottom-0 w-px bg-neutral-border" />

                  {/* Phase 1: Allowlist */}
                  <div className="relative flex gap-6 pb-8">
                    {/* Timeline Node */}
                    <div className="relative z-10 flex-shrink-0">
                      {(editForm.allowlist_enabled !== undefined
                        ? editForm.allowlist_enabled
                        : launch.allowlist_enabled) ? (
                        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-brand-500 text-white text-body-bold font-body-bold">
                          1
                        </div>
                      ) : (
                        <div className="w-8 h-8 rounded-full border-2 border-dashed border-neutral-400 dark:border-neutral-600 bg-transparent" />
                      )}
                    </div>

                    {/* Phase Content */}
                    <div className="flex-1 pt-1">
                      <div className="flex items-center justify-between mb-4">
                        <h4 className={`text-body-bold font-body-bold text-lg ${
                          (editForm.allowlist_enabled !== undefined
                            ? editForm.allowlist_enabled
                            : launch.allowlist_enabled)
                            ? 'text-default-font'
                            : 'text-subtext-color'
                        }`}>
                          Allowlist Phase
                          {!(editForm.allowlist_enabled !== undefined
                            ? editForm.allowlist_enabled
                            : launch.allowlist_enabled) && (
                            <span className="text-caption font-caption ml-2">(Skipped)</span>
                          )}
                        </h4>
                        <label className={`flex items-center gap-2 ${
                          (launch.status === 'active' || launch.status === 'paused')
                            ? 'cursor-not-allowed opacity-60'
                            : 'cursor-pointer'
                        }`}>
                          <input
                            type="checkbox"
                            checked={
                              editForm.allowlist_enabled !== undefined
                                ? editForm.allowlist_enabled
                                : launch.allowlist_enabled
                            }
                            disabled={launch.status === 'active' || launch.status === 'paused'}
                            onChange={(e) => {
                              // If disabling phase 1, also disable phase 2
                              if (!e.target.checked) {
                                setEditForm((prev) => ({
                                  ...prev,
                                  allowlist_enabled: false,
                                  allowlist_2_enabled: false,
                                }));
                              } else {
                                setEditForm((prev) => ({
                                  ...prev,
                                  allowlist_enabled: true,
                                }));
                              }
                            }}
                            className="w-5 h-5 rounded border-neutral-300 text-brand-600 focus:ring-brand-500 disabled:opacity-50"
                          />
                        </label>
                      </div>

                      {(editForm.allowlist_enabled !== undefined
                        ? editForm.allowlist_enabled
                        : launch.allowlist_enabled) ? (
                        <div className="space-y-4">
                          <TextField
                            label={
                              <span className="flex items-center gap-2">
                                Price (XRP)
                                {launch.mint_phase && (
                                  <span className="text-caption text-warning-600">(Locked)</span>
                                )}
                              </span>
                            }
                            helpText="Set to 0 for free mint. Supports tenths (e.g. 2.5). Prices lock permanently when this phase begins."
                          >
                            <TextField.Input
                              type="number"
                              min="0"
                              step="0.1"
                              placeholder="0"
                              disabled={!!launch.mint_phase}
                              value={String(
                                editForm.allowlist_mint_price_drops !== undefined
                                  ? dropsToXrpTenths(editForm.allowlist_mint_price_drops)
                                  : dropsToXrpTenths(launch.allowlist_mint_price_drops)
                              )}
                              onChange={(e) =>
                                setEditForm((prev) => ({
                                  ...prev,
                                  allowlist_mint_price_drops:
                                    e.target.value === ''
                                      ? 0
                                      : xrpTenthsToDrops(parseFloat(e.target.value) || 0),
                                }))
                              }
                            />
                          </TextField>

                          <p className="text-caption font-caption text-subtext-color">
                            {launch.allowlist_enabled ? (
                              <>
                                Manage addresses in the <button
                                  onClick={() => setActiveTab('allowlist')}
                                  className="text-brand-600 hover:underline"
                                >Allowlist tab</button>
                              </>
                            ) : (
                              <>Save to access the Allowlist tab</>
                            )}
                          </p>
                        </div>
                      ) : (
                        <p className="text-caption font-caption text-subtext-color">
                          Restrict early minting to specific addresses
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Phase 2: Allowlist 2 (Optional) - Only shown when Phase 1 is enabled */}
                  {(editForm.allowlist_enabled !== undefined
                    ? editForm.allowlist_enabled
                    : launch.allowlist_enabled) && (
                  <div className="relative flex gap-6 pb-8">
                    {/* Timeline Node */}
                    <div className="relative z-10 flex-shrink-0">
                      {(editForm.allowlist_2_enabled !== undefined
                        ? editForm.allowlist_2_enabled
                        : launch.allowlist_2_enabled) ? (
                        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-brand-500 text-white text-body-bold font-body-bold">
                          2
                        </div>
                      ) : (
                        <div className="w-8 h-8 rounded-full border-2 border-dashed border-neutral-400 dark:border-neutral-600 bg-transparent" />
                      )}
                    </div>

                    {/* Phase Content */}
                    <div className="flex-1 pt-1">
                      <div className="flex items-center justify-between mb-4">
                        <h4 className={`text-body-bold font-body-bold text-lg ${
                          (editForm.allowlist_2_enabled !== undefined
                            ? editForm.allowlist_2_enabled
                            : launch.allowlist_2_enabled)
                            ? 'text-default-font'
                            : 'text-subtext-color'
                        }`}>
                          Allowlist Phase 2
                          {!(editForm.allowlist_2_enabled !== undefined
                            ? editForm.allowlist_2_enabled
                            : launch.allowlist_2_enabled) && (
                            <span className="text-caption font-caption ml-2">(Skipped)</span>
                          )}
                        </h4>
                        <label className={`flex items-center gap-2 ${
                          (launch.mint_phase === 'allowlist_2' || launch.mint_phase === 'public')
                            ? 'cursor-not-allowed opacity-60'
                            : 'cursor-pointer'
                        }`}>
                          <input
                            type="checkbox"
                            checked={
                              editForm.allowlist_2_enabled !== undefined
                                ? editForm.allowlist_2_enabled
                                : launch.allowlist_2_enabled
                            }
                            disabled={launch.mint_phase === 'allowlist_2' || launch.mint_phase === 'public'}
                            onChange={(e) =>
                              setEditForm((prev) => ({
                                ...prev,
                                allowlist_2_enabled: e.target.checked,
                              }))
                            }
                            className="w-5 h-5 rounded border-neutral-300 text-brand-600 focus:ring-brand-500 disabled:opacity-50"
                          />
                        </label>
                      </div>

                      {(editForm.allowlist_2_enabled !== undefined
                        ? editForm.allowlist_2_enabled
                        : launch.allowlist_2_enabled) ? (
                        <div className="space-y-4">
                          <TextField
                            label={
                              <span className="flex items-center gap-2">
                                Price (XRP)
                                {(launch.mint_phase === 'allowlist_2' || launch.mint_phase === 'public') && (
                                  <span className="text-caption text-warning-600">(Locked)</span>
                                )}
                              </span>
                            }
                            helpText="Set to 0 for free mint. Supports tenths (e.g. 2.5). Prices lock permanently when this phase begins."
                          >
                            <TextField.Input
                              type="number"
                              min="0"
                              step="0.1"
                              placeholder="0"
                              disabled={launch.mint_phase === 'allowlist_2' || launch.mint_phase === 'public'}
                              value={String(
                                editForm.allowlist_2_mint_price_drops !== undefined
                                  ? dropsToXrpTenths(editForm.allowlist_2_mint_price_drops)
                                  : dropsToXrpTenths(launch.allowlist_2_mint_price_drops)
                              )}
                              onChange={(e) =>
                                setEditForm((prev) => ({
                                  ...prev,
                                  allowlist_2_mint_price_drops:
                                    e.target.value === ''
                                      ? 0
                                      : xrpTenthsToDrops(parseFloat(e.target.value) || 0),
                                }))
                              }
                            />
                          </TextField>

                          <p className="text-caption font-caption text-subtext-color">
                            {launch.allowlist_2_enabled ? (
                              <>
                                Manage addresses in the <button
                                  onClick={() => setActiveTab('allowlist')}
                                  className="text-brand-600 hover:underline"
                                >Allowlist tab</button> (Phase 2 section)
                              </>
                            ) : (
                              <>Save to access Phase 2 allowlist management</>
                            )}
                          </p>
                        </div>
                      ) : (
                        <p className="text-caption font-caption text-subtext-color">
                          Add a second whitelist phase with different price/limits
                        </p>
                      )}
                    </div>
                  </div>
                  )}

                  {/* Public Phase - number adjusts based on enabled phases */}
                  <div className="relative flex gap-6">
                    {/* Timeline Node */}
                    <div className="relative z-10 flex-shrink-0">
                      <div className="flex items-center justify-center w-8 h-8 rounded-full bg-brand-500 text-white text-body-bold font-body-bold">
                        {(() => {
                          const phase1 = editForm.allowlist_enabled !== undefined
                            ? editForm.allowlist_enabled
                            : launch.allowlist_enabled;
                          const phase2 = editForm.allowlist_2_enabled !== undefined
                            ? editForm.allowlist_2_enabled
                            : launch.allowlist_2_enabled;
                          if (phase1 && phase2) return 3;
                          if (phase1) return 2;
                          return 1;
                        })()}
                      </div>
                    </div>

                    {/* Phase Content */}
                    <div className="flex-1 pt-1">
                      <h4 className="text-body-bold font-body-bold text-default-font text-lg mb-4">
                        Public Phase
                      </h4>

                      <div className="space-y-4">
                        <TextField
                          label={
                            <span className="flex items-center gap-2">
                              Price (XRP)
                              {launch.mint_phase === 'public' && launch.public_phase_price_locked !== false && (
                                <span className="text-caption text-warning-600">(Locked)</span>
                              )}
                            </span>
                          }
                          helpText={launch.public_phase_price_locked !== false
                            ? "Set to 0 for free mint. Supports tenths (e.g. 2.5). Prices lock permanently when this phase begins."
                            : "Set to 0 for free mint. Supports tenths (e.g. 2.5). Price can be changed at any time."
                          }
                        >
                          <TextField.Input
                            type="number"
                            min="0"
                            step="0.1"
                            placeholder="0"
                            disabled={launch.mint_phase === 'public' && launch.public_phase_price_locked !== false}
                            value={String(
                              editForm.mint_price_drops !== undefined
                                ? dropsToXrpTenths(editForm.mint_price_drops)
                                : dropsToXrpTenths(launch.mint_price_drops)
                            )}
                            onChange={(e) =>
                              setEditForm((prev) => ({
                                ...prev,
                                mint_price_drops:
                                  e.target.value === ''
                                    ? 0
                                    : xrpTenthsToDrops(parseFloat(e.target.value) || 0),
                              }))
                            }
                          />
                        </TextField>

                        <TextField label="Max Per Wallet">
                          <TextField.Input
                            type="number"
                            min="1"
                            placeholder="Unlimited"
                            value={String(
                              editForm.limit_per_wallet !== undefined
                                ? editForm.limit_per_wallet ?? ''
                                : launch.limit_per_wallet ?? ''
                            )}
                            onChange={(e) =>
                              setEditForm((prev) => ({
                                ...prev,
                                limit_per_wallet:
                                  e.target.value === '' ? null : parseInt(e.target.value),
                              }))
                            }
                          />
                        </TextField>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <Button
                    onClick={handleSave}
                    disabled={saving || Object.keys(editForm).length === 0}
                    loading={saving}
                    icon={<FeatherSave />}
                  >
                    Save Changes
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="p-4 rounded-lg border border-neutral-border bg-default-background">
                  <h4 className="text-body-bold font-body-bold text-default-font mb-3">
                    Current Settings
                  </h4>
                  <div className="space-y-3">
                    {launch.allowlist_enabled && (
                      <>
                        <div className="flex justify-between">
                          <span className="text-body font-body text-subtext-color">
                            Allowlist Phase
                          </span>
                          <span className="text-body font-body text-default-font">
                            Enabled
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-body font-body text-subtext-color">
                            Allowlist Price
                          </span>
                          <span className="text-body font-body text-default-font">
                            {formatMintPrice(launch.allowlist_mint_price_drops)}
                          </span>
                        </div>
                      </>
                    )}
                    <div className="flex justify-between">
                      <span className="text-body font-body text-subtext-color">
                        Public Price
                      </span>
                      <span className="text-body font-body text-default-font">
                        {formatMintPrice(launch.mint_price_drops)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-body font-body text-subtext-color">
                        Royalty
                      </span>
                      <span className="text-body font-body text-default-font">
                        {(launch.transfer_fee ?? 0) / 1000}%
                      </span>
                    </div>
                    {((launch.nft_flags ?? 8) & 1) !== 0 && (
                      <div className="flex justify-between">
                        <span className="text-body font-body text-subtext-color">
                          Burnable by Issuer
                        </span>
                        <span className="text-body font-body text-brand-500">
                          Enabled
                        </span>
                      </div>
                    )}
                    {((launch.nft_flags ?? 8) & 16) !== 0 && (
                      <div className="flex justify-between">
                        <span className="text-body font-body text-subtext-color">
                          Mutable NFTs
                        </span>
                        <span className="text-body font-body text-brand-500">
                          Enabled
                        </span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-body font-body text-subtext-color">
                        Max Per Wallet (Public)
                      </span>
                      <span className="text-body font-body text-default-font">
                        {launch.limit_per_wallet ?? 'Unlimited'}
                      </span>
                    </div>
                    {launch.collection_type === 'composable' && (
                      <div className="flex justify-between">
                        <span className="text-body font-body text-subtext-color">
                          Build Your Own Fee
                        </span>
                        <span className="text-body font-body text-default-font">
                          {formatMintPrice(launch.composition_fee_per_nft_drops)}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
                <p className="text-caption font-caption text-subtext-color">
                  This launch has been approved and settings can no longer be changed.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Go Live / Schedule Tab - for ready/active/paused launches */}
        {activeTab === 'schedule' && ['ready', 'active', 'paused'].includes(launch.status) && (
          <div className="max-w-xl space-y-6">
            {/* Go Live Now section - only for ready launches */}
            {launch.status === 'ready' && (
              <div className="p-6 rounded-lg border border-neutral-border bg-default-background">
                <div className="flex items-center gap-3 mb-4">
                  <FeatherPlay className="w-5 h-5 text-success-600" />
                  <h3 className="text-body-bold font-body-bold text-default-font">
                    Go Live Now
                  </h3>
                </div>
                <p className="text-body font-body text-subtext-color mb-4">
                  Activate your launch immediately and start minting.
                  {launch.allowlist_enabled && launch.allowlist_2_enabled && (
                    <span className="block mt-2">
                      The launch will start in <strong>allowlist phase 1</strong>. Only addresses on your phase 1 allowlist will be able to mint initially.
                    </span>
                  )}
                  {launch.allowlist_enabled && !launch.allowlist_2_enabled && (
                    <span className="block mt-2">
                      The launch will start in <strong>allowlist phase</strong>. Only addresses on your allowlist will be able to mint initially.
                    </span>
                  )}
                  {!launch.allowlist_enabled && launch.allowlist_2_enabled && (
                    <span className="block mt-2">
                      The launch will start in <strong>allowlist phase 2</strong>. Only addresses on your phase 2 allowlist will be able to mint initially.
                    </span>
                  )}
                </p>
                <Button
                  onClick={() => {
                    setError(null);
                    setShowGoLiveConfirm(true);
                  }}
                  loading={activating}
                  icon={<FeatherPlay />}
                >
                  Go Live Now
                </Button>
              </div>
            )}

            {/* Schedule section */}
            <div className="p-6 rounded-lg border border-neutral-border bg-default-background">
              <div className="flex items-center gap-3 mb-4">
                <FeatherClock className="w-5 h-5 text-subtext-color" />
                <h3 className="text-body-bold font-body-bold text-default-font">
                  {launch.status === 'ready' ? 'Or Schedule for Later' : 'Schedule'}
                </h3>
              </div>

              {launch.status === 'ready' ? (
                <>
                  <p className="text-body font-body text-subtext-color mb-4">
                    Set dates for automatic activation. The launch will go live automatically when the scheduled time arrives.
                  </p>
                  <p className="text-caption font-caption text-subtext-color mb-6">
                    Times are in your local timezone ({userTimezone})
                  </p>
                  <div className="space-y-4">
                    {launch.allowlist_enabled && (
                      <TextField
                        label={launch.allowlist_2_enabled ? "Allowlist Phase 1 Start Time" : "Allowlist Start Time"}
                        helpText={launch.allowlist_2_enabled ? "When phase 1 allowlist minting begins." : "When allowlist minting begins automatically."}
                      >
                        <TextField.Input
                          type="datetime-local"
                          value={
                            editForm.allowlist_start_at !== undefined
                              ? editForm.allowlist_start_at ?? ''
                              : formatLocalDateTime(launch.allowlist_start_at)
                          }
                          onChange={(e) =>
                            setEditForm((prev) => ({
                              ...prev,
                              allowlist_start_at: e.target.value || null,
                            }))
                          }
                        />
                      </TextField>
                    )}
                    {launch.allowlist_2_enabled && (
                      <TextField
                        label="Allowlist Phase 2 Start Time"
                        helpText="When phase 2 allowlist minting begins (after phase 1)."
                      >
                        <TextField.Input
                          type="datetime-local"
                          value={
                            editForm.allowlist_2_start_at !== undefined
                              ? editForm.allowlist_2_start_at ?? ''
                              : formatLocalDateTime(launch.allowlist_2_start_at)
                          }
                          onChange={(e) =>
                            setEditForm((prev) => ({
                              ...prev,
                              allowlist_2_start_at: e.target.value || null,
                            }))
                          }
                        />
                      </TextField>
                    )}
                    <TextField
                      label="Public Start Time"
                      helpText={
                        launch.allowlist_enabled || launch.allowlist_2_enabled
                          ? 'When public minting begins (after allowlist phases).'
                          : 'When minting begins automatically.'
                      }
                    >
                      <TextField.Input
                        type="datetime-local"
                        value={
                          editForm.public_start_at !== undefined
                            ? editForm.public_start_at ?? ''
                            : formatLocalDateTime(launch.public_start_at)
                        }
                        onChange={(e) =>
                          setEditForm((prev) => ({
                            ...prev,
                            public_start_at: e.target.value || null,
                          }))
                        }
                      />
                    </TextField>
                    <TextField
                      label="End Time"
                      helpText="When minting ends automatically. Leave empty for no end time."
                    >
                      <TextField.Input
                        type="datetime-local"
                        value={
                          editForm.end_at !== undefined
                            ? editForm.end_at ?? ''
                            : formatLocalDateTime(launch.end_at)
                        }
                        onChange={(e) =>
                          setEditForm((prev) => ({
                            ...prev,
                            end_at: e.target.value || null,
                          }))
                        }
                      />
                    </TextField>
                    <div className="pt-2">
                      <Button
                        variant="neutral-secondary"
                        onClick={handleSave}
                        disabled={saving || Object.keys(editForm).length === 0}
                        loading={saving}
                        icon={<FeatherSave />}
                      >
                        Save Schedule
                      </Button>
                    </div>
                  </div>
                </>
              ) : (
                // Active or paused - show current phase and allow advancing/editing
                <div className="space-y-6">
                  {(() => {
                    const now = new Date();
                    const ended = launch.end_at && new Date(launch.end_at) <= now;
                    const canEditEnd = !ended;
                    const nextPhaseInfo = getNextPhaseInfo();

                    // Get current phase display name
                    const getCurrentPhaseName = () => {
                      switch (launch.mint_phase) {
                        case 'allowlist':
                          return launch.allowlist_2_enabled ? 'Allowlist Phase 1' : 'Allowlist';
                        case 'allowlist_2':
                          return 'Allowlist Phase 2';
                        case 'public':
                          return 'Public';
                        default:
                          return 'Unknown';
                      }
                    };

                    // Get when current phase started
                    const getCurrentPhaseStartTime = () => {
                      switch (launch.mint_phase) {
                        case 'allowlist':
                          return launch.allowlist_start_at;
                        case 'allowlist_2':
                          return launch.allowlist_2_start_at;
                        case 'public':
                          return launch.public_start_at;
                        default:
                          return launch.launched_at;
                      }
                    };

                    return (
                      <>
                        {/* Current Phase Indicator */}
                        <div className="p-4 rounded-lg bg-success-500/10 border border-success-500/30">
                          <div className="flex items-center gap-2 mb-1">
                            <div className="w-2 h-2 rounded-full bg-success-500 animate-pulse" />
                            <span className="text-body-bold font-body-bold text-default-font">
                              Current Phase: {getCurrentPhaseName()}
                            </span>
                          </div>
                          {getCurrentPhaseStartTime() && (
                            <p className="text-caption text-subtext-color ml-4">
                              Started: {new Date(getCurrentPhaseStartTime()!).toLocaleString()}
                            </p>
                          )}
                        </div>

                        {/* Next Phase Section - only if there's a next phase and launch is active (not paused) */}
                        {nextPhaseInfo && launch.status === 'active' && (
                          <div className="p-4 rounded-lg border border-neutral-border bg-default-background">
                            <div className="flex items-center justify-between mb-3">
                              <div>
                                <h4 className="text-body-bold font-body-bold text-default-font">
                                  Next: {nextPhaseInfo.nextPhaseName}
                                </h4>
                                <p className="text-caption text-subtext-color">
                                  {nextPhaseInfo.scheduledAt
                                    ? `Scheduled: ${new Date(nextPhaseInfo.scheduledAt).toLocaleString()}`
                                    : 'Not scheduled'}
                                </p>
                              </div>
                              <Button
                                onClick={() => setShowAdvancePhaseConfirm(true)}
                                loading={advancingPhase}
                                disabled={advancingPhase}
                                icon={<FeatherPlay />}
                              >
                                Start {nextPhaseInfo.nextPhaseName} Now
                              </Button>
                            </div>

                            {/* Schedule/Edit for next phase */}
                            {nextPhaseInfo.nextPhase === 'allowlist_2' && (
                              <div className="pt-3 border-t border-neutral-border">
                                <TextField
                                  label="Or schedule Phase 2 start time"
                                  helpText="Leave empty to advance manually."
                                >
                                  <TextField.Input
                                    type="datetime-local"
                                    value={
                                      editForm.allowlist_2_start_at !== undefined
                                        ? editForm.allowlist_2_start_at ?? ''
                                        : formatLocalDateTime(launch.allowlist_2_start_at)
                                    }
                                    onChange={(e) =>
                                      setEditForm((prev) => ({
                                        ...prev,
                                        allowlist_2_start_at: e.target.value || null,
                                      }))
                                    }
                                  />
                                </TextField>
                              </div>
                            )}
                            {nextPhaseInfo.nextPhase === 'public' && (
                              <div className="pt-3 border-t border-neutral-border">
                                <TextField
                                  label="Or schedule Public start time"
                                  helpText="Leave empty to advance manually."
                                >
                                  <TextField.Input
                                    type="datetime-local"
                                    value={
                                      editForm.public_start_at !== undefined
                                        ? editForm.public_start_at ?? ''
                                        : formatLocalDateTime(launch.public_start_at)
                                    }
                                    onChange={(e) =>
                                      setEditForm((prev) => ({
                                        ...prev,
                                        public_start_at: e.target.value || null,
                                      }))
                                    }
                                  />
                                </TextField>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Public phase - no next phase available */}
                        {!nextPhaseInfo && launch.mint_phase === 'public' && (
                          <p className="text-body text-subtext-color">
                            This is the final phase. The launch will complete when all items are minted or the end time is reached.
                          </p>
                        )}

                        {/* End time */}
                        <div className="pt-2">
                          {canEditEnd ? (
                            <TextField
                              label="End Time"
                              helpText="When minting ends automatically. Leave empty for no end time."
                            >
                              <TextField.Input
                                type="datetime-local"
                                value={
                                  editForm.end_at !== undefined
                                    ? editForm.end_at ?? ''
                                    : formatLocalDateTime(launch.end_at)
                                }
                                onChange={(e) =>
                                  setEditForm((prev) => ({
                                    ...prev,
                                    end_at: e.target.value || null,
                                  }))
                                }
                              />
                            </TextField>
                          ) : launch.end_at ? (
                            <div className="flex justify-between py-2 border-b border-neutral-border">
                              <span className="text-body font-body text-subtext-color">Ended</span>
                              <span className="text-body font-body text-default-font">
                                {new Date(launch.end_at).toLocaleString()}
                              </span>
                            </div>
                          ) : null}
                        </div>

                        {/* Save button for schedule changes */}
                        {Object.keys(editForm).length > 0 && (
                          <div className="pt-2">
                            <Button
                              variant="neutral-secondary"
                              onClick={handleSave}
                              disabled={saving}
                              loading={saving}
                              icon={<FeatherSave />}
                            >
                              Save Schedule
                            </Button>
                          </div>
                        )}

                        <p className="text-caption font-caption text-subtext-color">
                          Times are in your local timezone ({userTimezone})
                        </p>
                      </>
                    );
                  })()}
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'traits' && launch.collection_type === 'composable' && (
          <WizardStepComposableTraits
            launchId={launch.id}
            baseImageUrl={launch.base_image_url}
            onBaseImageUploaded={(url) => {
              setLaunch((prev) => {
                if (!prev) return null;
                const updates: Partial<typeof prev> = { base_image_url: url };
                if (!prev.thumbnail_url) updates.thumbnail_url = url;
                return { ...prev, ...updates };
              });
            }}
            onError={setError}
          />
        )}

        {activeTab === 'editions' && launch.collection_type === 'edition' && (
          <WizardStepEditions
            launchId={launch.id}
            defaultPriceDrops={launch.mint_price_drops != null ? Number(launch.mint_price_drops) : null}
            onUploadComplete={() => { fetchLaunch(); }}
            onError={setError}
          />
        )}

        {activeTab === 'assets' && (
          <div className="space-y-6">
            {/* Upload Area (only for drafts) */}
            {isDraft && (
              <div
                {...assetDragHandlers}
                className={`relative p-8 rounded-lg border-2 border-dashed transition-colors ${
                  isDragging
                    ? 'border-brand-500 bg-brand-50 dark:bg-brand-900/20'
                    : 'border-neutral-300 dark:border-neutral-600 hover:border-brand-400'
                }`}
              >
                {assetUploading ? (
                  <div className="flex flex-col items-center gap-4">
                    <LoadingSpinner size="md" />
                    <p className="text-body font-body text-default-font">
                      {uploadProgress.statusMessage || 'Uploading...'}
                    </p>
                    {uploadProgress.totalItems > 0 && (
                      <>
                        <div className="w-full max-w-xs bg-neutral-200 rounded-full h-2">
                          <div
                            className="bg-brand-500 h-2 rounded-full transition-all"
                            style={{ width: `${uploadProgress.percent}%` }}
                          />
                        </div>
                        <p className="text-caption font-caption text-subtext-color">
                          {uploadProgress.completedItems} of {uploadProgress.totalItems}
                          {uploadProgress.failedItems > 0 && ` (${uploadProgress.failedItems} failed)`}
                        </p>
                      </>
                    )}
                    <Button
                      variant="neutral-tertiary"
                      size="small"
                      onClick={cancelUpload}
                    >
                      Cancel
                    </Button>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-4">
                    <FeatherUpload className="w-12 h-12 text-neutral-400" />
                    <div className="text-center">
                      <p className="text-body font-body text-default-font">
                        Drag and drop your NFT folder here
                      </p>
                      <p className="text-caption font-caption text-subtext-color mt-1">
                        Folder should contain <code className="font-mono text-brand-500">images/</code> and <code className="font-mono text-brand-500">metadata/</code> directories
                      </p>
                    </div>
                    {failedCount > 0 && (
                      <div className="flex items-center gap-3">
                        <p className="text-caption font-caption text-error-600">
                          {failedCount} items failed to upload
                        </p>
                        <Button
                          variant="brand-secondary"
                          size="small"
                          onClick={retryFailed}
                        >
                          Retry Failed
                        </Button>
                      </div>
                    )}
                    <div className="flex items-center gap-4">
                      <Button
                        variant="neutral-secondary"
                        onClick={() => assetsInputRef.current?.click()}
                      >
                        Select Folder
                      </Button>
                    </div>
                    <input
                      ref={assetsInputRef}
                      type="file"
                      // @ts-ignore - webkitdirectory is a non-standard attribute
                      webkitdirectory=""
                      directory=""
                      multiple
                      className="hidden"
                      onChange={(e) => e.target.files && handleFolderUpload(e.target.files)}
                    />
                  </div>
                )}
              </div>
            )}

            {/* Upload Errors */}
            {uploadErrors.length > 0 && (
              <div className="p-4 bg-warning-100 dark:bg-warning-900/20 border border-warning-200 dark:border-warning-800 rounded-lg">
                <p className="text-body-bold font-body-bold text-warning-700 dark:text-warning-400 mb-2">
                  Some items had issues:
                </p>
                <ul className="list-disc list-inside space-y-1">
                  {uploadErrors.slice(0, 10).map((err, i) => (
                    <li key={i} className="text-caption font-caption text-warning-600 dark:text-warning-500">
                      {err}
                    </li>
                  ))}
                  {uploadErrors.length > 10 && (
                    <li className="text-caption font-caption text-warning-600 dark:text-warning-500">
                      ...and {uploadErrors.length - 10} more
                    </li>
                  )}
                </ul>
              </div>
            )}

            {/* Assets Header */}
            <div className="flex items-center justify-between">
              <h3 className="text-body-bold font-body-bold text-default-font">
                {assetUploading && uploadProgress.totalItems > 0
                  ? `${uploadProgress.completedItems.toLocaleString()} of ${uploadProgress.totalItems.toLocaleString()} Uploaded`
                  : `${assetsTotal.toLocaleString()} ${assetsTotal === 1 ? 'Asset' : 'Assets'}`
                }
              </h3>
              {isDraft && assetsTotal > 0 && (
                <Button
                  variant="neutral-tertiary"
                  size="small"
                  onClick={handleClearAssets}
                  loading={clearingAssets}
                >
                  Clear All
                </Button>
              )}
            </div>

            {/* Assets Grid */}
            {assetsLoading ? (
              <div className="flex items-center justify-center py-12">
                <LoadingSpinner size="md" />
              </div>
            ) : assets.length === 0 ? (
              <div className="p-8 rounded-lg border border-neutral-border bg-default-background text-center">
                <p className="text-body font-body text-subtext-color">
                  No assets uploaded yet.
                  {isDraft && ' Drag and drop a folder above to get started.'}
                </p>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                  {assets.map((asset) => (
                    <button
                      key={asset.id}
                      onClick={() => setSelectedAsset(asset)}
                      className="group relative aspect-square rounded-lg overflow-hidden border border-neutral-border bg-default-background hover:border-brand-500 transition-colors"
                    >
                      {asset.original_image_key && is3DModelUrl(asset.original_image_key) ? (
                        asset.thumbnail_key ? (
                          <img
                            src={asset.thumbnail_key}
                            alt={asset.name || `Token ${asset.token_number}`}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex flex-col items-center justify-center bg-neutral-100 dark:bg-neutral-800 gap-1">
                            <span style={{ fontSize: '32px' }}>🧊</span>
                            <span className="text-caption font-caption text-subtext-color">3D Model</span>
                          </div>
                        )
                      ) : asset.original_image_key ? (
                        <img
                          src={asset.original_image_key}
                          alt={asset.name || `Token ${asset.token_number}`}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-neutral-100 dark:bg-neutral-800">
                          <span className="text-heading-3 font-heading-3 text-neutral-400">
                            {asset.token_number}
                          </span>
                        </div>
                      )}
                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-end">
                        <div className="p-2 w-full">
                          <p className="text-caption font-caption text-white truncate">
                            {asset.name}
                          </p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>

                {/* Pagination */}
                {assetsTotal > ASSETS_PER_PAGE && (
                  <div className="flex items-center justify-between pt-4">
                    <p className="text-caption font-caption text-subtext-color">
                      Showing {((assetsPage - 1) * ASSETS_PER_PAGE) + 1}-{Math.min(assetsPage * ASSETS_PER_PAGE, assetsTotal)} of {assetsTotal.toLocaleString()}
                    </p>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="neutral-secondary"
                        size="small"
                        disabled={assetsPage <= 1}
                        onClick={() => setAssetsPage(p => p - 1)}
                      >
                        Previous
                      </Button>
                      <span className="text-caption font-caption text-subtext-color px-2">
                        Page {assetsPage} of {Math.ceil(assetsTotal / ASSETS_PER_PAGE)}
                      </span>
                      <Button
                        variant="neutral-secondary"
                        size="small"
                        disabled={assetsPage >= Math.ceil(assetsTotal / ASSETS_PER_PAGE)}
                        onClick={() => setAssetsPage(p => p + 1)}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {activeTab === 'allowlist' && (() => {
          // Determine if current phase is read-only based on mint phase progression
          // Phase 1 is read-only when mint_phase is 'allowlist_2' or 'public'
          // Phase 2 is read-only when mint_phase is 'public'
          const isPhase1ReadOnly = launch.mint_phase === 'allowlist_2' || launch.mint_phase === 'public';
          const isPhase2ReadOnly = launch.mint_phase === 'public';
          const isCurrentPhaseReadOnly = allowlistPhase2Selected ? isPhase2ReadOnly : isPhase1ReadOnly;
          const canEdit = !isCurrentPhaseReadOnly && ['draft', 'pending_approval', 'pending_authorization', 'ready', 'active', 'paused'].includes(launch.status);

          return (
          <div className="space-y-6">
            {/* Phase Selector - shown only when both phases are enabled */}
            {launch.allowlist_enabled && launch.allowlist_2_enabled && (
              <div className="inline-flex rounded-lg border border-neutral-border overflow-hidden">
                <button
                  onClick={() => setAllowlistPhase2Selected(false)}
                  className={`px-4 py-2 text-body-bold font-body-bold transition-colors ${
                    !allowlistPhase2Selected
                      ? 'bg-brand-500 text-white'
                      : 'bg-default-background text-subtext-color hover:bg-neutral-100'
                  }`}
                >
                  Phase 1
                </button>
                <button
                  onClick={() => setAllowlistPhase2Selected(true)}
                  className={`px-4 py-2 text-body-bold font-body-bold transition-colors border-l border-neutral-border ${
                    allowlistPhase2Selected
                      ? 'bg-brand-500 text-white'
                      : 'bg-default-background text-subtext-color hover:bg-neutral-100'
                  }`}
                >
                  Phase 2
                </button>
              </div>
            )}

            {/* Show which phase is being managed when only one is enabled */}
            {launch.allowlist_enabled && !launch.allowlist_2_enabled && (
              <p className="text-caption font-caption text-subtext-color">
                Managing Phase 1 Allowlist
              </p>
            )}

            {/* Read-only banner when phase has ended */}
            {isCurrentPhaseReadOnly && (
              <div className="p-4 rounded-lg border border-neutral-border bg-default-background">
                <p className="text-body font-body text-subtext-color">
                  This phase has ended. The allowlist is read-only, but you can still export the data.
                </p>
              </div>
            )}

            {/* Stats Cards */}
            {allowlistStats && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="p-6 rounded-lg border border-neutral-border bg-default-background">
                  <p className="text-caption font-caption text-subtext-color mb-1">
                    Total Addresses
                  </p>
                  <p className="text-heading-2 font-heading-2 text-default-font">
                    {allowlistStats.total_entries.toLocaleString()}
                  </p>
                </div>
                <div className="p-6 rounded-lg border border-neutral-border bg-default-background">
                  <p className="text-caption font-caption text-subtext-color mb-1">
                    Can Still Mint
                  </p>
                  <p className="text-heading-2 font-heading-2 text-green-600">
                    {allowlistStats.entries_with_mints_left.toLocaleString()}
                  </p>
                </div>
                <div className="p-6 rounded-lg border border-neutral-border bg-default-background">
                  <p className="text-caption font-caption text-subtext-color mb-1">
                    Total Remaining Mints
                  </p>
                  <p className="text-heading-2 font-heading-2 text-brand-600">
                    {allowlistStats.total_remaining_mints.toLocaleString()}
                  </p>
                  <p className="text-caption font-caption text-subtext-color mt-1">
                    excludes unlimited
                  </p>
                </div>
              </div>
            )}

            {/* Public Registration Settings */}
            {canEdit && (() => {
              const isPhase2 = allowlistPhase2Selected;
              const regEnabled = isPhase2
                ? launch.allowlist_2_registration_enabled
                : launch.allowlist_registration_enabled;
              const regLimit = isPhase2
                ? launch.allowlist_2_registration_limit
                : launch.allowlist_registration_limit;
              const regDefaultMints = isPhase2
                ? (launch.allowlist_2_registration_default_mints ?? 1)
                : (launch.allowlist_registration_default_mints ?? 1);
              const regDeadline = isPhase2
                ? launch.allowlist_2_registration_deadline
                : launch.allowlist_registration_deadline;
              const enabledField = isPhase2
                ? 'allowlist_2_registration_enabled' as const
                : 'allowlist_registration_enabled' as const;
              const limitField = isPhase2
                ? 'allowlist_2_registration_limit' as const
                : 'allowlist_registration_limit' as const;
              const mintsField = isPhase2
                ? 'allowlist_2_registration_default_mints' as const
                : 'allowlist_registration_default_mints' as const;
              const deadlineField = isPhase2
                ? 'allowlist_2_registration_deadline' as const
                : 'allowlist_registration_deadline' as const;
              const reqField = isPhase2
                ? 'allowlist_2_registration_requirements' as const
                : 'allowlist_registration_requirements' as const;
              const currentRequirements: RegistrationRequirement[] =
                (isPhase2
                  ? launch.allowlist_2_registration_requirements
                  : launch.allowlist_registration_requirements) || [];
              const regLink = `${window.location.origin}/drops/${launch.slug}/register`;

              const requirementTypeLabels: Record<RequirementType, string> = {
                holds_collection: 'Must Hold NFT from Collection',
                minted_on_deals: 'Must Have Minted on xrp.deals',
                minted_from_collection: 'Must Have Minted from Collection',
                offers_copied: 'Must Have Copied Listings to xrp.deals',
                twitter_follow: 'Must Follow on X (Twitter)',
                paid_registration: 'Paid Registration (charge XRP)',
              };

              const addRequirement = (type: RequirementType) => {
                const defaults: Record<RequirementType, RegistrationRequirement> = {
                  holds_collection: { type: 'holds_collection', collectionIds: [], minCount: 1 },
                  minted_on_deals: { type: 'minted_on_deals' },
                  minted_from_collection: { type: 'minted_from_collection', collectionIds: [] },
                  offers_copied: { type: 'offers_copied' },
                  twitter_follow: { type: 'twitter_follow', twitterHandle: launch.twitter_handle || '' },
                  paid_registration: { type: 'paid_registration', priceDrops: 5000000, maxQuantity: 10 },
                };
                const newReqs = [...currentRequirements, defaults[type]];
                setLaunch((prev) => prev ? { ...prev, [reqField]: newReqs } : null);
              };

              const updateRequirement = (index: number, updated: RegistrationRequirement) => {
                const newReqs = [...currentRequirements];
                newReqs[index] = updated;
                setLaunch((prev) => prev ? { ...prev, [reqField]: newReqs } : null);
              };

              const removeRequirement = (index: number) => {
                const newReqs = currentRequirements.filter((_, i) => i !== index);
                setLaunch((prev) => prev ? { ...prev, [reqField]: newReqs } : null);
              };

              return (
                <div>
                  <hr className="border-neutral-border mb-6" />
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-body-bold font-body-bold text-default-font">
                      Public Registration Page
                    </h3>
                    <Button
                      variant={regEnabled ? 'destructive-secondary' : 'brand-primary'}
                      size="small"
                      loading={savingRegistration}
                      onClick={async () => {
                        const val = !regEnabled;
                        setSavingRegistration(true);
                        try {
                          const updated = await updateLaunch(launch.id, { [enabledField]: val });
                          setLaunch((prev) => (prev ? { ...prev, ...updated } : null));
                        } catch (err) {
                          setError(err instanceof Error ? err.message : 'Failed to update');
                        } finally {
                          setSavingRegistration(false);
                        }
                      }}
                    >
                      {regEnabled ? 'Disable' : 'Enable'}
                    </Button>
                  </div>

                  {regEnabled && (
                    <div className="space-y-4">
                      {/* Share Link */}
                      <div>
                        <label className="text-caption font-caption text-subtext-color block mb-1">
                          Share Link
                        </label>
                        <div className="flex items-center gap-2">
                          <input
                            type="text"
                            readOnly
                            value={regLink}
                            className="flex-1 px-3 py-2 rounded-lg border border-neutral-border bg-default-background text-body text-default-font font-mono text-sm"
                          />
                          <Button
                            variant="neutral-secondary"
                            size="small"
                            onClick={async () => {
                              await navigator.clipboard.writeText(regLink);
                              setCopiedRegLink(true);
                              setTimeout(() => setCopiedRegLink(false), 2000);
                            }}
                          >
                            {copiedRegLink ? 'Copied!' : 'Copy'}
                          </Button>
                        </div>
                      </div>

                      {/* Settings Row */}
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div>
                          <label className="text-caption font-caption text-subtext-color block mb-1">
                            Max Registrations
                          </label>
                          <input
                            type="number"
                            min="1"
                            placeholder="Unlimited"
                            value={regLimit ?? ''}
                            onChange={(e) => {
                              const val = e.target.value ? parseInt(e.target.value, 10) : null;
                              setLaunch((prev) => prev ? { ...prev, [limitField]: val } : null);
                            }}
                            className="w-full px-3 py-2 rounded-lg border border-neutral-border bg-default-background text-body text-default-font"
                          />
                        </div>
                        <div>
                          <label className="text-caption font-caption text-subtext-color block mb-1">
                            Mints per Registrant
                          </label>
                          <input
                            type="number"
                            min="1"
                            value={regDefaultMints}
                            onChange={(e) => {
                              const val = parseInt(e.target.value, 10) || 1;
                              setLaunch((prev) => prev ? { ...prev, [mintsField]: val } : null);
                            }}
                            className="w-full px-3 py-2 rounded-lg border border-neutral-border bg-default-background text-body text-default-font"
                          />
                        </div>
                        <div>
                          <label className="text-caption font-caption text-subtext-color block mb-1">
                            Registration Deadline
                          </label>
                          <input
                            type="datetime-local"
                            value={regDeadline ? formatLocalDateTime(regDeadline) : ''}
                            onChange={(e) => {
                              const val = e.target.value || null;
                              setLaunch((prev) => prev ? { ...prev, [deadlineField]: val ? new Date(val).toISOString() : null } : null);
                            }}
                            className="w-full px-3 py-2 rounded-lg border border-neutral-border bg-default-background text-body text-default-font"
                          />
                        </div>
                      </div>

                      {/* Registration Requirements */}
                      <div className="pt-4 border-t border-neutral-border">
                        <div className="flex items-center justify-between mb-3">
                          <label className="text-caption-bold font-caption-bold text-default-font flex items-center gap-2">
                            Registration Requirements
                            <span className="inline-flex items-center rounded-full border border-green-600 bg-default-background px-2 py-0.5 text-[10px] font-semibold tracking-wide text-green-600">✨ Beta</span>
                          </label>
                          <SubframeCore.DropdownMenu.Root>
                            <SubframeCore.DropdownMenu.Trigger asChild>
                              <Button variant="neutral-secondary" size="small" icon={<FeatherPlus />}>
                                Add Requirement
                              </Button>
                            </SubframeCore.DropdownMenu.Trigger>
                            <SubframeCore.DropdownMenu.Portal>
                              <SubframeCore.DropdownMenu.Content
                                side="bottom"
                                align="end"
                                sideOffset={4}
                                asChild
                              >
                                <DropdownMenu>
                                  {/* General requirements */}
                                  {!currentRequirements.some((r) => r.type === 'paid_registration') && (
                                    <DropdownMenu.DropdownItem icon={null} onSelect={() => addRequirement('paid_registration')}>
                                      Paid Registration (charge XRP)
                                    </DropdownMenu.DropdownItem>
                                  )}
                                  {!currentRequirements.some((r) => r.type === 'twitter_follow') && (
                                    <DropdownMenu.DropdownItem icon={null} onSelect={() => addRequirement('twitter_follow')}>
                                      Must Follow on X (Twitter)
                                    </DropdownMenu.DropdownItem>
                                  )}
                                  <DropdownMenu.DropdownItem icon={null} onSelect={() => addRequirement('holds_collection')}>
                                    Must Hold NFT from Collection
                                  </DropdownMenu.DropdownItem>
                                  <DropdownMenu.DropdownItem icon={null} onSelect={() => addRequirement('minted_from_collection')}>
                                    Must Have Minted from Collection
                                  </DropdownMenu.DropdownItem>

                                  {/* Only render xrp.deals Marketplace requirements for specific wallet address */}
                                  {useAuthStore.getState().user?.xrpl_address === 'rNvbPBdxJRwS5nU1dGBHDBtaAh4B9vvRoB' && (
                                    <>
                                      <DropdownMenu.DropdownDivider />
                                      <div className="px-3 py-1">
                                        <span className="text-caption font-caption text-subtext-color">xrp.deals Marketplace</span>
                                      </div>
                                      {!currentRequirements.some((r) => r.type === 'offers_copied') && (
                                        <DropdownMenu.DropdownItem icon={null} onSelect={() => addRequirement('offers_copied')}>
                                          Must Have Copied Listings to xrp.deals
                                        </DropdownMenu.DropdownItem>
                                      )}
                                      {!currentRequirements.some((r) => r.type === 'minted_on_deals') && (
                                        <DropdownMenu.DropdownItem icon={null} onSelect={() => addRequirement('minted_on_deals')}>
                                          Must Have Minted on xrp.deals
                                        </DropdownMenu.DropdownItem>
                                      )}
                                    </>
                                  )}
                                </DropdownMenu>
                              </SubframeCore.DropdownMenu.Content>
                            </SubframeCore.DropdownMenu.Portal>
                          </SubframeCore.DropdownMenu.Root>
                        </div>

                        {currentRequirements.length === 0 ? (
                          <p className="text-caption font-caption text-subtext-color">
                            No requirements configured. Anyone can register.
                          </p>
                        ) : (
                          <div className="space-y-3">
                            {currentRequirements.map((req, idx) => (
                              <div
                                key={`${req.type}-${idx}`}
                                className="p-4 rounded-lg border border-neutral-border bg-default-background"
                              >
                                <div className="flex items-center justify-between mb-3">
                                  <span className="text-body-bold font-body-bold text-default-font">
                                    {requirementTypeLabels[req.type]}
                                  </span>
                                  <button
                                    type="button"
                                    onClick={() => removeRequirement(idx)}
                                    className="text-subtext-color hover:text-red-500 transition-colors"
                                  >
                                    <FeatherX className="w-4 h-4" />
                                  </button>
                                </div>

                                {/* Collection picker for holds_collection */}
                                {req.type === 'holds_collection' && (
                                  <div className="space-y-3">
                                    <div>
                                      <label className="text-caption font-caption text-subtext-color block mb-1">
                                        Collections (user must hold from any of these)
                                      </label>
                                      <CollectionSearchSelect
                                        selectedIds={req.collectionIds || []}
                                        onChange={(ids) =>
                                          updateRequirement(idx, { ...req, collectionIds: ids })
                                        }
                                      />
                                    </div>
                                    <div>
                                      <label className="text-caption font-caption text-subtext-color block mb-1">
                                        Minimum NFTs
                                      </label>
                                      <input
                                        type="number"
                                        min="1"
                                        value={req.minCount ?? 1}
                                        onChange={(e) =>
                                          updateRequirement(idx, {
                                            ...req,
                                            minCount: Math.max(1, parseInt(e.target.value, 10) || 1),
                                          })
                                        }
                                        className="w-24 px-3 py-2 rounded-lg border border-neutral-border bg-default-background text-body text-default-font"
                                      />
                                    </div>
                                  </div>
                                )}

                                {/* Collection picker for minted_from_collection */}
                                {req.type === 'minted_from_collection' && (
                                  <div>
                                    <label className="text-caption font-caption text-subtext-color block mb-1">
                                      Collections (user must have minted from any of these)
                                    </label>
                                    <CollectionSearchSelect
                                      selectedIds={req.collectionIds || []}
                                      onChange={(ids) =>
                                        updateRequirement(idx, { ...req, collectionIds: ids })
                                      }
                                    />
                                  </div>
                                )}

                                {/* Twitter handle for twitter_follow */}
                                {req.type === 'twitter_follow' && (
                                  <div className="space-y-3">
                                    <div>
                                      <label className="text-caption font-caption text-subtext-color block mb-1">
                                        X Handle
                                      </label>
                                      <div className="relative max-w-xs">
                                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-subtext-color text-body font-body">@</span>
                                        <input
                                          type="text"
                                          value={req.twitterHandle || ''}
                                          onChange={(e) =>
                                            updateRequirement(idx, {
                                              ...req,
                                              twitterHandle: e.target.value.replace(/[^a-zA-Z0-9_]/g, ''),
                                            })
                                          }
                                          placeholder="username"
                                          maxLength={50}
                                          className="w-full pl-8 pr-3 py-2 rounded-lg border border-neutral-border bg-default-background text-body text-default-font"
                                        />
                                      </div>
                                    </div>
                                    <p className="text-caption font-caption text-subtext-color">
                                      Note: Due to X API limitations, follows cannot be verified automatically. Users will be prompted to follow and asked to provide their X username, which you can use to verify manually. This is still effective at driving follows.
                                    </p>
                                  </div>
                                )}

                                {req.type === 'offers_copied' && (
                                  <p className="text-caption font-caption text-subtext-color">
                                    Requires users to copy any existing NFT sell offers from other marketplaces to xrp.deals. This helps grow xrp.deals listings and gives users access to our lower 1% broker fee. Users with no listings elsewhere automatically pass this requirement.
                                  </p>
                                )}

                                {req.type === 'minted_on_deals' && (
                                  <p className="text-caption font-caption text-subtext-color">
                                    Requires users to have minted at least one NFT from any collection launched on xrp.deals. This rewards active participants in our launchpad ecosystem and helps drive engagement for all launches on the platform.
                                  </p>
                                )}

                                {/* Paid registration config */}
                                {req.type === 'paid_registration' && (
                                  <div className="space-y-3">
                                    <div>
                                      <label className="text-caption font-caption text-subtext-color block mb-1">
                                        Price per mint slot (XRP)
                                      </label>
                                      <input
                                        type="number"
                                        min="0.001"
                                        step="0.1"
                                        value={(req.priceDrops ?? 5000000) / 1_000_000}
                                        onChange={(e) => {
                                          const xrp = parseFloat(e.target.value) || 0;
                                          updateRequirement(idx, {
                                            ...req,
                                            priceDrops: Math.round(xrp * 1_000_000),
                                          });
                                        }}
                                        className="w-32 px-3 py-2 rounded-lg border border-neutral-border bg-default-background text-body text-default-font"
                                      />
                                    </div>
                                    <div>
                                      <label className="text-caption font-caption text-subtext-color block mb-1">
                                        Max slots per user (leave empty for no limit)
                                      </label>
                                      <input
                                        type="number"
                                        min="1"
                                        value={req.maxQuantity ?? ''}
                                        onChange={(e) => {
                                          const val = parseInt(e.target.value, 10);
                                          updateRequirement(idx, {
                                            ...req,
                                            maxQuantity: isNaN(val) ? undefined : Math.max(1, val),
                                          });
                                        }}
                                        placeholder="No limit"
                                        className="w-32 px-3 py-2 rounded-lg border border-neutral-border bg-default-background text-body text-default-font placeholder:text-subtext-color"
                                      />
                                    </div>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Save */}
                      <div className="flex items-center justify-end gap-3">
                        {registrationSaved && (
                          <span className="flex items-center gap-1 text-caption font-caption text-green-600">
                            <FeatherCheck className="w-4 h-4" />
                            Saved
                          </span>
                        )}
                        <Button
                          size="small"
                          loading={savingRegistration}
                          onClick={async () => {
                            setSavingRegistration(true);
                            setRegistrationSaved(false);
                            try {
                              const payload: UpdateLaunchInput = {
                                [limitField]: regLimit,
                                [mintsField]: regDefaultMints,
                                [deadlineField]: regDeadline ? new Date(regDeadline).toISOString() : null,
                                [reqField]: currentRequirements.length > 0 ? currentRequirements : null,
                              };
                              const updated = await updateLaunch(launch.id, payload);
                              setLaunch((prev) => (prev ? { ...prev, ...updated } : null));
                              setRegistrationSaved(true);
                              setTimeout(() => setRegistrationSaved(false), 3000);
                            } catch (err) {
                              setError(err instanceof Error ? err.message : 'Failed to save');
                            } finally {
                              setSavingRegistration(false);
                            }
                          }}
                        >
                          Save Registration Settings
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Add Entry / Import Section */}
            {canEdit && (
              <div>
                <hr className="border-neutral-border mb-6" />
                <h3 className="text-body-bold font-body-bold text-default-font mb-4">
                  Add Addresses
                </h3>

                {/* Manual Entry */}
                <div className="flex flex-col sm:flex-row gap-3 mb-4">
                  <div className="flex-1">
                    <TextField>
                      <TextField.Input
                        placeholder="rXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXX"
                        value={newAddress}
                        onChange={(e) => setNewAddress(e.target.value)}
                      />
                    </TextField>
                  </div>
                  <div className="w-32">
                    <TextField>
                      <TextField.Input
                        type="number"
                        min="1"
                        placeholder="Max mint"
                        value={newMaxMint}
                        onChange={(e) => setNewMaxMint(e.target.value)}
                      />
                    </TextField>
                  </div>
                  <Button
                    onClick={handleAddEntry}
                    disabled={!newAddress.trim() || addingEntry}
                    loading={addingEntry}
                  >
                    Add
                  </Button>
                </div>

                {/* CSV Import */}
                <div className="flex items-center gap-3 pt-4 border-t border-neutral-border">
                  <Button
                    variant="neutral-secondary"
                    size="small"
                    onClick={() => csvInputRef.current?.click()}
                    loading={csvUploading}
                  >
                    Import CSV
                  </Button>
                  <input
                    ref={csvInputRef}
                    type="file"
                    accept=".csv"
                    className="hidden"
                    onChange={handleCsvUploadWithPreview}
                  />
                  <span className="text-caption font-caption text-subtext-color">
                    CSV columns: address, max_mint
                  </span>
                </div>
              </div>
            )}

            {/* Search and Filter Row */}
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <TextField>
                  <TextField.Input
                    placeholder="Search by address..."
                    value={allowlistSearch}
                    onChange={(e) => handleAllowlistSearch(e.target.value)}
                  />
                </TextField>
              </div>
              <div className="w-48">
                <select
                  className="w-full px-3 py-2 rounded-lg border border-neutral-border bg-default-background text-default-font text-body"
                  value={allowlistFilter}
                  onChange={(e) => handleAllowlistFilterChange(e.target.value as AllowlistFilterStatus)}
                >
                  <option value="all">All Addresses</option>
                  <option value="has_minted">Has Minted</option>
                  <option value="hasnt_minted">Hasn&apos;t Minted</option>
                  <option value="maxed_out">Maxed Out</option>
                </select>
              </div>
            </div>

            {/* Bulk Actions Bar */}
            {selectedAddresses.size > 0 && canEdit && (
              <div className="flex items-center gap-4 p-4 rounded-lg border border-brand-500 bg-brand-50 dark:bg-brand-900/20">
                <span className="text-body font-body text-default-font">
                  {selectedAddresses.size} selected
                </span>
                <div className="flex items-center gap-2">
                  <Button
                    variant="destructive-secondary"
                    size="small"
                    onClick={handleBulkDelete}
                    loading={bulkDeleting}
                  >
                    Delete Selected
                  </Button>
                  <Button
                    variant="neutral-secondary"
                    size="small"
                    onClick={() => setShowBulkLimitModal(true)}
                    disabled={bulkUpdating}
                  >
                    Set Limit
                  </Button>
                  <Button
                    variant="neutral-tertiary"
                    size="small"
                    onClick={handleClearSelection}
                  >
                    Clear
                  </Button>
                </div>
              </div>
            )}

            {/* Allowlist Header */}
            <div className="flex items-center justify-between">
              <h3 className="text-body-bold font-body-bold text-default-font">
                {allowlistTotal} {allowlistTotal === 1 ? 'Address' : 'Addresses'}
                {(allowlistSearch || allowlistFilter !== 'all') && ` (filtered)`}
              </h3>
              {allowlistEntries.length > 0 && (
                <div className="flex items-center gap-2">
                  {canEdit && (
                    <Button
                      variant="neutral-tertiary"
                      size="small"
                      onClick={() => setShowClearConfirm(true)}
                      loading={clearingAllowlist}
                    >
                      Clear All
                    </Button>
                  )}
                  <Button
                    variant="neutral-tertiary"
                    size="small"
                    onClick={handleExportCsv}
                  >
                    Export CSV
                  </Button>
                </div>
              )}
            </div>

            {/* Allowlist Table */}
            {allowlistLoading ? (
              <div className="flex items-center justify-center py-12">
                <LoadingSpinner size="md" />
              </div>
            ) : allowlistEntries.length === 0 ? (
              <div className="p-8 rounded-lg border border-neutral-border bg-default-background text-center">
                <p className="text-body font-body text-subtext-color">
                  {allowlistSearch || allowlistFilter !== 'all'
                    ? 'No addresses match the current filter.'
                    : 'No addresses in allowlist.'}
                  {canEdit &&
                    !allowlistSearch &&
                    allowlistFilter === 'all' &&
                    ' Add addresses above to restrict who can mint.'}
                </p>
              </div>
            ) : (
              <div className="rounded-lg border border-neutral-border overflow-hidden">
                <table className="w-full">
                  <thead className="border-b border-neutral-border">
                    <tr>
                      {canEdit && (
                        <th className="px-4 py-3 w-10">
                          <input
                            type="checkbox"
                            checked={selectedAddresses.size === allowlistEntries.length && allowlistEntries.length > 0}
                            onChange={handleSelectAll}
                            className="w-4 h-4 rounded border-neutral-border"
                          />
                        </th>
                      )}
                      <th className="px-4 py-3 text-left text-caption font-caption text-subtext-color">
                        Address
                      </th>
                      <th className="px-4 py-3 text-right text-caption font-caption text-subtext-color">
                        Max Mint
                      </th>
                      <th className="px-4 py-3 text-right text-caption font-caption text-subtext-color">
                        Minted
                      </th>
                      {canEdit && (
                        <th className="px-4 py-3 w-16"></th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-neutral-border">
                    {allowlistEntries.map((entry) => (
                      <tr
                        key={entry.id}
                        className={`bg-default-background group ${
                          selectedAddresses.has(entry.wallet_address) ? 'bg-brand-50 dark:bg-brand-900/20' : ''
                        }`}
                      >
                        {canEdit && (
                          <td className="px-4 py-3">
                            <input
                              type="checkbox"
                              checked={selectedAddresses.has(entry.wallet_address)}
                              onChange={() => handleToggleSelection(entry.wallet_address)}
                              className="w-4 h-4 rounded border-neutral-border"
                            />
                          </td>
                        )}
                        <td className="px-4 py-3 text-body font-mono text-default-font">
                          <div className="flex items-center gap-2">
                            <span>{entry.wallet_address.slice(0, 8)}...{entry.wallet_address.slice(-6)}</span>
                            <button
                              onClick={() => handleCopyAddress(entry.wallet_address)}
                              className="p-1 hover:bg-neutral-100 rounded transition-colors"
                              title={copiedAllowlistAddress === entry.wallet_address ? 'Copied!' : 'Copy address'}
                            >
                              {copiedAllowlistAddress === entry.wallet_address ? (
                                <FeatherCheck className="w-4 h-4 text-success-600" />
                              ) : (
                                <FeatherCopy className="w-4 h-4 text-subtext-color hover:text-default-font" />
                              )}
                            </button>
                          </div>
                          {entry.registration_metadata?.twitter_username && (
                            <span className="text-caption font-caption text-subtext-color font-sans">
                              @{entry.registration_metadata.twitter_username}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right text-body text-default-font">
                          {editingAddress === entry.wallet_address ? (
                            <div className="flex items-center justify-end gap-2">
                              <input
                                type="number"
                                min="0"
                                value={editingLimit}
                                onChange={(e) => setEditingLimit(e.target.value)}
                                className="w-20 px-2 py-1 text-right rounded border border-neutral-border bg-default-background text-default-font"
                                autoFocus
                              />
                              <button
                                onClick={handleSaveEdit}
                                disabled={savingEdit}
                                className="text-green-500 hover:text-green-600 disabled:opacity-50"
                                title="Save"
                              >
                                {savingEdit ? '...' : '✓'}
                              </button>
                              <button
                                onClick={handleCancelEdit}
                                className="text-red-500 hover:text-red-600"
                                title="Cancel"
                              >
                                ✕
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => handleStartEdit(entry)}
                              className={`hover:text-brand-600 ${
                                canEdit
                                  ? 'cursor-pointer'
                                  : 'cursor-default'
                              }`}
                              disabled={!canEdit}
                              title={canEdit ? 'Click to edit' : ''}
                            >
                              {entry.mint_limit === 0 ? 'Unlimited' : entry.mint_limit}
                            </button>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right text-body text-default-font">
                          {entry.minted_count}
                        </td>
                        {canEdit && (
                          <td className="px-4 py-3 text-right">
                            <button
                              onClick={() => setAddressToRemove(entry.wallet_address)}
                              className="text-subtext-color hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                              title="Remove address"
                            >
                              ✕
                            </button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Pagination */}
                {allowlistTotalPages > 1 && (
                  <div className="flex items-center justify-center gap-2 p-4 border-t border-neutral-border">
                    <Button
                      variant="neutral-secondary"
                      size="small"
                      disabled={allowlistPage === 1 || allowlistLoading}
                      onClick={() => handleAllowlistPageChange(allowlistPage - 1)}
                    >
                      Previous
                    </Button>
                    <span className="text-body font-body text-subtext-color px-4">
                      Page {allowlistPage} of {allowlistTotalPages}
                    </span>
                    <Button
                      variant="neutral-secondary"
                      size="small"
                      disabled={allowlistPage === allowlistTotalPages || allowlistLoading}
                      onClick={() => handleAllowlistPageChange(allowlistPage + 1)}
                    >
                      Next
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
          );
        })()}

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-default-background rounded-lg p-6 max-w-md w-full mx-4 border border-neutral-border">
              <h3 className="text-heading-3 font-heading-3 text-default-font mb-2">
                Delete Collection?
              </h3>
              <p className="text-body font-body text-subtext-color mb-6">
                This action cannot be undone. All assets and allowlist entries will be
                deleted.
              </p>
              <div className="flex items-center justify-end gap-2">
                <Button
                  variant="neutral-secondary"
                  onClick={() => setShowDeleteConfirm(false)}
                  disabled={deleting}
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive-primary"
                  onClick={handleDelete}
                  loading={deleting}
                >
                  Delete
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Go Live Confirmation Modal */}
        {showGoLiveConfirm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-default-background rounded-lg p-6 max-w-md w-full mx-4 border border-neutral-border">
              <h3 className="text-heading-3 font-heading-3 text-default-font mb-2">
                Confirm Go Live
              </h3>
              <p className="text-body font-body text-subtext-color mb-4">
                This will activate your launch immediately and make it available for minting.
              </p>
              {(launch?.allowlist_start_at || (launch?.public_start_at && !launch?.allowlist_enabled)) && (
                <div className="p-3 rounded-lg border border-warning-300 bg-warning-50 dark:bg-warning-900/20 mb-4">
                  <p className="text-body font-body text-warning-700 dark:text-warning-600">
                    This will override your scheduled start time of{' '}
                    <strong>
                      {new Date(
                        launch?.allowlist_start_at || launch?.public_start_at || ''
                      ).toLocaleString()}
                    </strong>.
                  </p>
                </div>
              )}
              <div className="flex items-center justify-end gap-2">
                <Button
                  variant="neutral-secondary"
                  onClick={() => setShowGoLiveConfirm(false)}
                  disabled={activating}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleGoLive}
                  loading={activating}
                >
                  Go Live
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Advance Phase Confirmation Modal */}
        {showAdvancePhaseConfirm && (() => {
          const nextPhaseInfo = getNextPhaseInfo();
          if (!nextPhaseInfo) return null;

          return (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
              <div className="bg-default-background rounded-lg p-6 max-w-md w-full mx-4 border border-neutral-border">
                <h3 className="text-heading-3 font-heading-3 text-default-font mb-2">
                  Start {nextPhaseInfo.nextPhaseName}?
                </h3>
                <p className="text-body font-body text-subtext-color mb-4">
                  This will immediately transition your launch to {nextPhaseInfo.nextPhaseName}. This action cannot be undone.
                </p>
                {nextPhaseInfo.scheduledAt && (
                  <div className="p-3 rounded-lg border border-warning-300 bg-warning-50 dark:bg-warning-900/20 mb-4">
                    <p className="text-body font-body text-warning-700 dark:text-warning-600">
                      This will override your scheduled time of{' '}
                      <strong>{new Date(nextPhaseInfo.scheduledAt).toLocaleString()}</strong>.
                    </p>
                  </div>
                )}
                <div className="flex items-center justify-end gap-2">
                  <Button
                    variant="neutral-secondary"
                    onClick={() => setShowAdvancePhaseConfirm(false)}
                    disabled={advancingPhase}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleAdvancePhase}
                    loading={advancingPhase}
                  >
                    Start {nextPhaseInfo.nextPhaseName}
                  </Button>
                </div>
              </div>
            </div>
          );
        })()}

        {/* Clear Allowlist Confirmation Modal */}
        {showClearConfirm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-default-background rounded-lg p-6 max-w-md w-full mx-4 border border-neutral-border">
              <h3 className="text-heading-3 font-heading-3 text-default-font mb-2">
                Clear Allowlist?
              </h3>
              <p className="text-body font-body text-subtext-color mb-6">
                This will remove all {allowlistTotal} {allowlistTotal === 1 ? 'address' : 'addresses'} from the allowlist. This action cannot be undone.
              </p>
              <div className="flex items-center justify-end gap-2">
                <Button
                  variant="neutral-secondary"
                  onClick={() => setShowClearConfirm(false)}
                  disabled={clearingAllowlist}
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive-primary"
                  onClick={handleClearAllowlist}
                  loading={clearingAllowlist}
                >
                  Clear All
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Remove Address Confirmation Modal */}
        {addressToRemove && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-default-background rounded-lg p-6 max-w-md w-full mx-4 border border-neutral-border">
              <h3 className="text-heading-3 font-heading-3 text-default-font mb-4">
                Remove from Allowlist?
              </h3>
              <p className="text-body font-mono text-default-font mb-6 break-all">
                {addressToRemove}
              </p>
              <div className="flex items-center justify-end gap-2">
                <Button
                  variant="neutral-secondary"
                  onClick={() => setAddressToRemove(null)}
                  disabled={removingEntry}
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive-primary"
                  onClick={handleRemoveEntry}
                  loading={removingEntry}
                >
                  Remove
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Bulk Set Limit Modal */}
        {showBulkLimitModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-default-background rounded-lg p-6 max-w-md w-full mx-4 border border-neutral-border">
              <h3 className="text-heading-3 font-heading-3 text-default-font mb-2">
                Set Mint Limit
              </h3>
              <p className="text-body font-body text-subtext-color mb-4">
                Set the mint limit for {selectedAddresses.size} selected {selectedAddresses.size === 1 ? 'address' : 'addresses'}.
              </p>
              <div className="mb-6">
                <TextField label="Mint Limit" helpText="Enter 0 for unlimited mints">
                  <TextField.Input
                    type="number"
                    min="0"
                    placeholder="Enter mint limit"
                    value={bulkMintLimit}
                    onChange={(e) => setBulkMintLimit(e.target.value)}
                    autoFocus
                  />
                </TextField>
              </div>
              <div className="flex items-center justify-end gap-2">
                <Button
                  variant="neutral-secondary"
                  onClick={() => {
                    setShowBulkLimitModal(false);
                    setBulkMintLimit('');
                  }}
                  disabled={bulkUpdating}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleBulkUpdateLimit}
                  loading={bulkUpdating}
                  disabled={!bulkMintLimit}
                >
                  Update
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Import Preview Modal */}
        {showImportPreview && importPreview && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-default-background rounded-lg p-6 max-w-lg w-full mx-4 border border-neutral-border">
              <h3 className="text-heading-3 font-heading-3 text-default-font mb-4">
                Import Preview
              </h3>
              <div className="space-y-4 mb-6">
                <div className="grid grid-cols-3 gap-4">
                  <div className="p-4 rounded-lg border border-green-500 bg-green-50 dark:bg-green-900/20">
                    <p className="text-caption font-caption text-green-700 dark:text-green-400 mb-1">
                      New
                    </p>
                    <p className="text-heading-3 font-heading-3 text-green-600">
                      {importPreview.newCount}
                    </p>
                  </div>
                  <div className="p-4 rounded-lg border border-blue-500 bg-blue-50 dark:bg-blue-900/20">
                    <p className="text-caption font-caption text-blue-700 dark:text-blue-400 mb-1">
                      Update
                    </p>
                    <p className="text-heading-3 font-heading-3 text-blue-600">
                      {importPreview.updateCount}
                    </p>
                  </div>
                  <div className="p-4 rounded-lg border border-red-500 bg-red-50 dark:bg-red-900/20">
                    <p className="text-caption font-caption text-red-700 dark:text-red-400 mb-1">
                      Invalid
                    </p>
                    <p className="text-heading-3 font-heading-3 text-red-600">
                      {importPreview.invalidCount}
                    </p>
                  </div>
                </div>
                {importPreview.invalidEntries.length > 0 && (
                  <div className="p-4 rounded-lg border border-red-200 bg-red-50 dark:bg-red-900/20">
                    <p className="text-caption font-caption text-red-700 dark:text-red-400 mb-2">
                      Invalid entries:
                    </p>
                    <ul className="space-y-1 max-h-32 overflow-y-auto">
                      {importPreview.invalidEntries.slice(0, 10).map((entry, idx) => (
                        <li key={idx} className="text-caption font-mono text-red-600 dark:text-red-400">
                          {entry.address.slice(0, 12)}... - {entry.reason}
                        </li>
                      ))}
                      {importPreview.invalidEntries.length > 10 && (
                        <li className="text-caption font-caption text-red-600 dark:text-red-400">
                          ...and {importPreview.invalidEntries.length - 10} more
                        </li>
                      )}
                    </ul>
                  </div>
                )}
                <p className="text-body font-body text-subtext-color">
                  {importPreview.newCount + importPreview.updateCount} valid {importPreview.newCount + importPreview.updateCount === 1 ? 'address' : 'addresses'} will be imported.
                  {importPreview.updateCount > 0 && ` ${importPreview.updateCount} existing ${importPreview.updateCount === 1 ? 'entry' : 'entries'} will be updated.`}
                </p>
              </div>
              <div className="flex items-center justify-end gap-2">
                <Button
                  variant="neutral-secondary"
                  onClick={handleCancelImport}
                  disabled={csvUploading}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleConfirmImport}
                  loading={csvUploading}
                  disabled={importPreview.newCount + importPreview.updateCount === 0}
                >
                  Import {importPreview.newCount + importPreview.updateCount} Addresses
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Revenue Tab */}
        {activeTab === 'revenue' && ['active', 'paused', 'completed'].includes(launch.status) && (
          <div className="space-y-6">
            {revenueLoading && !revenueStats ? (
              <div className="flex items-center justify-center py-12">
                <LoadingSpinner size="lg" />
              </div>
            ) : revenueStats ? (
              <>
                {/* Revenue Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Total Revenue */}
                  <div className="p-6 rounded-lg border border-neutral-border bg-default-background">
                    <p className="text-caption font-caption text-subtext-color mb-1">
                      Total Revenue
                    </p>
                    <p className="text-heading-2 font-heading-2 text-default-font">
                      {revenueStats.total_revenue_xrp.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 6,
                      })}{' '}
                      XRP
                    </p>
                    <p className="text-caption font-caption text-subtext-color mt-1">
                      from {revenueStats.completed_mints} mints
                    </p>
                  </div>

                  {/* Settled */}
                  <div className="p-6 rounded-lg border border-green-500/30 bg-green-500/5">
                    <p className="text-caption font-caption text-subtext-color mb-1">
                      Settled (Paid Out)
                    </p>
                    <p className="text-heading-2 font-heading-2 text-green-600">
                      {revenueStats.settled_revenue_xrp.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 6,
                      })}{' '}
                      XRP
                    </p>
                    <p className="text-caption font-caption text-subtext-color mt-1">
                      {revenueStats.total_settlements} settlement{revenueStats.total_settlements !== 1 ? 's' : ''}
                    </p>
                  </div>

                  {/* Pending */}
                  <div className="p-6 rounded-lg border border-yellow-500/30 bg-yellow-500/5">
                    <p className="text-caption font-caption text-subtext-color mb-1">
                      Pending Settlement
                    </p>
                    <p className="text-heading-2 font-heading-2 text-yellow-600">
                      {revenueStats.pending_settlement_xrp.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 6,
                      })}{' '}
                      XRP
                    </p>
                    <p className="text-caption font-caption text-subtext-color mt-1">
                      will be settled automatically
                    </p>
                  </div>
                </div>

                {/* Stats Summary */}
                <div className="p-6 rounded-lg border border-neutral-border bg-default-background">
                  <h3 className="text-body-bold font-body-bold text-default-font mb-4">
                    Mint Statistics
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <p className="text-caption font-caption text-subtext-color">
                        Total Supply
                      </p>
                      <p className="text-heading-3 font-heading-3 text-default-font">
                        {launch.total_supply ?? '—'}
                      </p>
                    </div>
                    <div>
                      <p className="text-caption font-caption text-subtext-color">
                        Completed
                      </p>
                      <p className="text-heading-3 font-heading-3 text-green-600">
                        {revenueStats.completed_mints}
                      </p>
                    </div>
                    <div>
                      <p className="text-caption font-caption text-subtext-color">
                        Failed
                      </p>
                      <p className="text-heading-3 font-heading-3 text-red-600">
                        {revenueStats.failed_mints}
                      </p>
                    </div>
                    <div>
                      <p className="text-caption font-caption text-subtext-color">
                        Unique Minters
                      </p>
                      <p className="text-heading-3 font-heading-3 text-default-font">
                        {revenueStats.unique_minters}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Settlement History */}
                <div className="p-6 rounded-lg border border-neutral-border bg-default-background">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-body-bold font-body-bold text-default-font">
                      Settlement History
                    </h3>
                    {settlements.length > 0 && (
                      <Button
                        variant="neutral-secondary"
                        size="small"
                        onClick={handleExportSettlementsCsv}
                        disabled={exportingCsv}
                      >
                        {exportingCsv ? 'Exporting...' : 'Export CSV'}
                      </Button>
                    )}
                  </div>
                  {settlements.length === 0 ? (
                    <p className="text-body font-body text-subtext-color text-center py-8">
                      No settlements yet. Payments are settled automatically every 5 minutes when the minimum threshold (1 XRP) is reached.
                    </p>
                  ) : (
                    <div className="space-y-4">
                      {/* Settlement List */}
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead>
                            <tr className="border-b border-neutral-border">
                              <th className="text-left py-2 px-2 text-caption font-caption text-subtext-color">
                                Date
                              </th>
                              <th className="text-left py-2 px-2 text-caption font-caption text-subtext-color">
                                Amount
                              </th>
                              <th className="text-left py-2 px-2 text-caption font-caption text-subtext-color">
                                Mints
                              </th>
                              <th className="text-left py-2 px-2 text-caption font-caption text-subtext-color">
                                Transaction
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {settlements.map((settlement) => (
                              <tr
                                key={settlement.id}
                                className="border-b border-neutral-border hover:bg-neutral-50"
                              >
                                <td className="py-3 px-2 text-body font-body text-default-font">
                                  {new Date(settlement.created_at).toLocaleDateString()}{' '}
                                  <span className="text-caption text-subtext-color">
                                    {new Date(settlement.created_at).toLocaleTimeString()}
                                  </span>
                                </td>
                                <td className="py-3 px-2 text-body font-body text-green-600">
                                  +{(settlement.amount_drops / 1_000_000).toLocaleString(undefined, {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 6,
                                  })}{' '}
                                  XRP
                                </td>
                                <td className="py-3 px-2 text-body font-body text-default-font">
                                  {settlement.mint_count}
                                </td>
                                <td className="py-3 px-2">
                                  {settlement.tx_hash ? (
                                    <a
                                      href={`https://xrpscan.com/tx/${settlement.tx_hash}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="inline-flex items-center gap-1 text-brand-600 hover:text-brand-700"
                                    >
                                      <span className="text-body font-body">
                                        {settlement.tx_hash.slice(0, 8)}...{settlement.tx_hash.slice(-8)}
                                      </span>
                                      <FeatherExternalLink className="w-3 h-3" />
                                    </a>
                                  ) : (
                                    <span className="text-body font-body text-subtext-color">—</span>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      {/* Pagination */}
                      {settlementsTotalPages > 1 && (
                        <div className="flex items-center justify-center gap-2 mt-4">
                          <Button
                            variant="neutral-secondary"
                            size="small"
                            disabled={settlementsPage === 1 || revenueLoading}
                            onClick={() => fetchRevenue(settlementsPage - 1)}
                          >
                            Previous
                          </Button>
                          <span className="text-body font-body text-subtext-color px-4">
                            Page {settlementsPage} of {settlementsTotalPages}
                          </span>
                          <Button
                            variant="neutral-secondary"
                            size="small"
                            disabled={settlementsPage === settlementsTotalPages || revenueLoading}
                            onClick={() => fetchRevenue(settlementsPage + 1)}
                          >
                            Next
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="text-center py-12">
                <p className="text-body font-body text-subtext-color">
                  Failed to load revenue data
                </p>
                <Button
                  variant="neutral-secondary"
                  className="mt-4"
                  onClick={() => fetchRevenue(1)}
                >
                  Retry
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Ambassadors Tab */}
        {activeTab === 'ambassadors' && (
          <LaunchAmbassadorsTab launch={launch} onUpdate={() => fetchLaunch()} />
        )}

        {/* Referrals Tab */}
        {activeTab === 'referrals' && (
          <div className="space-y-6">
            <div className="p-6 rounded-lg border border-neutral-border bg-default-background">
              <h3 className="text-heading-3 font-heading-3 text-default-font mb-2">Referral Program</h3>
              <p className="text-body font-body text-subtext-color mb-6">
                Enable referrals to let your community earn rewards for bringing in new minters.
                Referrers share a unique link, and when someone mints through that link, they earn
                a percentage of your mint revenue. The referral reward comes from your share of the
                mint price (after platform fees).
              </p>

              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <input
                    id="referral-toggle"
                    type="checkbox"
                    checked={referralEnabled}
                    onChange={(e) => {
                      setReferralEnabled(e.target.checked);
                      setReferralDirty(true);
                    }}
                    className="w-5 h-5 rounded border-neutral-border text-brand-600 focus:ring-brand-500 cursor-pointer"
                  />
                  <label htmlFor="referral-toggle" className="text-body font-body text-default-font cursor-pointer">Enable Referrals</label>
                </div>

                {referralEnabled && (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-body font-body text-default-font mb-1">
                        Referral Reward (%)
                      </label>
                      <input
                        type="number"
                        min={1}
                        max={100}
                        step={1}
                        value={referralBps / 100}
                        onChange={(e) => {
                          const pct = Math.max(1, Math.min(100, Math.round(Number(e.target.value))));
                          setReferralBps(pct * 100);
                          setReferralDirty(true);
                        }}
                        className="w-32 px-3 py-2 rounded-md border border-neutral-border bg-default-background text-default-font focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
                      />
                      <p className="text-caption font-caption text-subtext-color mt-1">
                        1% to 100%. The percentage of the mint price that referrers earn per mint.
                      </p>
                    </div>

                    {/* Live preview */}
                    {(() => {
                      const mintPrice = Number(launch.mint_price_drops) || 0;
                      if (mintPrice <= 0) return null;
                      // mint_price_drops IS the creator's share — platform fee is added on top for the buyer
                      const referralFee = Math.floor(mintPrice * referralBps / 10000);
                      const creatorAfterReferral = mintPrice - referralFee;
                      const fmt = (drops: number) => {
                        const xrp = drops / 1_000_000;
                        return xrp % 1 === 0 ? xrp.toFixed(0) : parseFloat(xrp.toFixed(6)).toString();
                      };
                      return (
                        <p className="text-caption font-caption text-subtext-color">
                          At {fmt(mintPrice)} XRP per mint, referrers earn {fmt(referralFee)} XRP ({referralBps / 100}%) and you receive {fmt(creatorAfterReferral)} XRP.
                        </p>
                      );
                    })()}
                  </div>
                )}

                <div className="flex items-center gap-3 mt-4">
                  <Button
                    icon={<FeatherSave />}
                    loading={referralSaving}
                    disabled={!referralDirty}
                    onClick={async () => {
                      if (!launch?.id) return;
                      setReferralSaving(true);
                      try {
                        await updateLaunchReferralConfig(launch.id, {
                          referral_enabled: referralEnabled,
                          referral_bps: referralEnabled ? referralBps : null,
                        });
                        setLaunch((prev) =>
                          prev
                            ? {
                                ...prev,
                                referral_enabled: referralEnabled,
                                referral_bps: referralEnabled ? referralBps : null,
                              }
                            : null
                        );
                        setReferralDirty(false);
                      } catch (err) {
                        console.error('Failed to save referral config:', err);
                        setError(err instanceof Error ? err.message : 'Failed to save referral config');
                      } finally {
                        setReferralSaving(false);
                      }
                    }}
                  >
                    Save
                  </Button>
                  {referralDirty && (
                    <Button
                      variant="neutral-secondary"
                      onClick={() => {
                        setReferralEnabled(launch.referral_enabled ?? false);
                        setReferralBps(launch.referral_bps ?? 500);
                        setReferralDirty(false);
                      }}
                    >
                      Cancel
                    </Button>
                  )}
                </div>

              </div>
            </div>

            {/* Referral Stats */}
            {launch.referral_enabled && (() => {
              const stats = referralStats?.launch_stats;
              const referrers = referralStats?.top_referrers;

              if (!stats || (stats.referred_mints === 0 && stats.direct_mints === 0)) return null;

              const totalMints = stats.referred_mints + stats.direct_mints;
              const referralRate = totalMints > 0 ? Math.round((stats.referred_mints / totalMints) * 100) : 0;
              const fmt = (drops: number) => {
                const xrp = drops / 1_000_000;
                return xrp % 1 === 0 ? xrp.toFixed(0) : parseFloat(xrp.toFixed(6)).toString();
              };

              return (
                <div className="p-6 rounded-lg border border-neutral-border bg-default-background">
                  <h3 className="text-heading-3 font-heading-3 text-default-font mb-4">Referral Stats</h3>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                    <div className="rounded-lg border border-neutral-border bg-neutral-50 p-4 text-center">
                      <p className="text-heading-3 font-heading-3 text-default-font">{stats.referred_mints}</p>
                      <p className="text-caption font-caption text-subtext-color">Referred Mints</p>
                    </div>
                    <div className="rounded-lg border border-neutral-border bg-neutral-50 p-4 text-center">
                      <p className="text-heading-3 font-heading-3 text-default-font">{fmt(stats.total_referral_fee_drops)} XRP</p>
                      <p className="text-caption font-caption text-subtext-color">Total Referral Fees</p>
                    </div>
                    <div className="rounded-lg border border-neutral-border bg-neutral-50 p-4 text-center">
                      <p className="text-heading-3 font-heading-3 text-default-font">{stats.unique_referrers}</p>
                      <p className="text-caption font-caption text-subtext-color">Unique Referrers</p>
                    </div>
                    <div className="rounded-lg border border-neutral-border bg-neutral-50 p-4 text-center">
                      <p className="text-heading-3 font-heading-3 text-default-font">{referralRate}%</p>
                      <p className="text-caption font-caption text-subtext-color">Referral Rate</p>
                    </div>
                  </div>

                  {referrers && referrers.length > 0 && (
                    <>
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-body-bold font-body-bold text-default-font">Top Referrers</h4>
                        <button
                          onClick={() => {
                            const rows = [['Referrer', 'Address', 'Mints', 'Accrued (XRP)']];
                            referrers.forEach((r) => {
                              rows.push([
                                r.username || '',
                                r.xrpl_address,
                                String(r.referred_mints),
                                (r.total_earned_drops / 1_000_000).toFixed(6),
                              ]);
                            });
                            const csv = rows.map((r) => r.join(',')).join('\n');
                            const blob = new Blob([csv], { type: 'text/csv' });
                            const url = URL.createObjectURL(blob);
                            const a = document.createElement('a');
                            a.href = url;
                            a.download = `referrers-${launch.slug}.csv`;
                            a.click();
                            URL.revokeObjectURL(url);
                          }}
                          className="text-caption font-caption text-brand-600 hover:text-brand-700"
                        >
                          Export CSV
                        </button>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full border-collapse">
                          <thead>
                            <tr className="border-b border-neutral-border">
                              <th className="text-left py-2 px-3 text-caption font-caption text-subtext-color uppercase tracking-wider">Referrer</th>
                              <th className="text-right py-2 px-3 text-caption font-caption text-subtext-color uppercase tracking-wider">Mints</th>
                              <th className="text-right py-2 px-3 text-caption font-caption text-subtext-color uppercase tracking-wider">Accrued</th>
                            </tr>
                          </thead>
                          <tbody>
                            {referrers.map((r) => (
                              <tr key={r.xrpl_address} className="border-b border-neutral-border">
                                <td className="py-2 px-3 text-body font-body text-default-font">
                                  {r.username || `${r.xrpl_address.slice(0, 8)}...${r.xrpl_address.slice(-4)}`}
                                </td>
                                <td className="py-2 px-3 text-right text-body font-body text-default-font">{r.referred_mints}</td>
                                <td className="py-2 px-3 text-right text-body font-body text-brand-600">{fmt(r.total_earned_drops)} XRP</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}
                </div>
              );
            })()}
          </div>
        )}

        {/* Mint Contest Tab */}
        {activeTab === 'mint-contest' && (
          <MintRewardsManager launchId={launch.id} launchStatus={launch.status} />
        )}

        {/* Royalties Tab */}
        {activeTab === 'royalties' && (launch.transfer_fee || 0) > 0 && (
          <div className="space-y-6">
            {royaltiesLoading && !royaltyStats ? (
              <div className="flex items-center justify-center py-12">
                <LoadingSpinner size="lg" />
              </div>
            ) : royaltyStats ? (
              <>
                {/* Info Box - at top for visibility */}
                <div className="p-4 rounded-lg border border-neutral-border bg-neutral-100">
                  <p className="text-caption font-caption text-subtext-color">
                    <strong className="text-default-font">Note:</strong> Royalties are automatically transferred to you on each secondary sale via the XRPL NFT transfer fee mechanism.
                    The amounts shown here are calculated based on tracked marketplace transactions.
                  </p>
                </div>

                {/* Royalty Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Total Royalties */}
                  <div className="p-6 rounded-lg border border-neutral-border bg-default-background">
                    <p className="text-caption font-caption text-subtext-color mb-1">
                      Total Royalties Earned
                    </p>
                    <p className="text-heading-2 font-heading-2 text-green-600">
                      {royaltyStats.totalRoyaltiesXrp.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 6,
                      })}{' '}
                      XRP
                    </p>
                    <p className="text-caption font-caption text-subtext-color mt-1">
                      from secondary sales
                    </p>
                  </div>

                  {/* Secondary Sales Count */}
                  <div className="p-6 rounded-lg border border-neutral-border bg-default-background">
                    <p className="text-caption font-caption text-subtext-color mb-1">
                      Secondary Sales
                    </p>
                    <p className="text-heading-2 font-heading-2 text-default-font">
                      {royaltyStats.totalSecondarySales.toLocaleString()}
                    </p>
                    <p className="text-caption font-caption text-subtext-color mt-1">
                      total resales
                    </p>
                  </div>

                  {/* Royalty Rate */}
                  <div className="p-6 rounded-lg border border-neutral-border bg-default-background">
                    <p className="text-caption font-caption text-subtext-color mb-1">
                      Royalty Rate
                    </p>
                    <p className="text-heading-2 font-heading-2 text-brand-600">
                      {royaltyStats.transferFeePercent.toFixed(2)}%
                    </p>
                    <p className="text-caption font-caption text-subtext-color mt-1">
                      on each sale
                    </p>
                  </div>
                </div>

                {/* Volume Stats */}
                <div className="p-6 rounded-lg border border-neutral-border bg-default-background">
                  <h3 className="text-body-bold font-body-bold text-default-font mb-4">
                    Secondary Market Activity
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div>
                      <p className="text-caption font-caption text-subtext-color">
                        Total Volume
                      </p>
                      <p className="text-heading-3 font-heading-3 text-default-font">
                        {royaltyStats.totalSecondaryVolumeXrp.toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}{' '}
                        XRP
                      </p>
                    </div>
                    <div>
                      <p className="text-caption font-caption text-subtext-color">
                        Average Sale Price
                      </p>
                      <p className="text-heading-3 font-heading-3 text-default-font">
                        {royaltyStats.totalSecondarySales > 0
                          ? (
                              royaltyStats.totalSecondaryVolumeXrp /
                              royaltyStats.totalSecondarySales
                            ).toLocaleString(undefined, {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })
                          : '0.00'}{' '}
                        XRP
                      </p>
                    </div>
                    <div>
                      <p className="text-caption font-caption text-subtext-color">
                        Last Sale
                      </p>
                      <p className="text-heading-3 font-heading-3 text-default-font">
                        {royaltyStats.lastSaleAt
                          ? new Date(royaltyStats.lastSaleAt).toLocaleDateString()
                          : 'N/A'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Recent Sales */}
                {royaltyStats.recentSales && royaltyStats.recentSales.length > 0 && (
                  <div className="p-6 rounded-lg border border-neutral-border bg-default-background">
                    <h3 className="text-body-bold font-body-bold text-default-font mb-4">
                      Recent Secondary Sales
                    </h3>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-neutral-border">
                            <th className="text-left py-2 px-2 text-caption font-caption text-subtext-color">
                              Date
                            </th>
                            <th className="text-left py-2 px-2 text-caption font-caption text-subtext-color">
                              NFT
                            </th>
                            <th className="text-right py-2 px-2 text-caption font-caption text-subtext-color">
                              Sale Price
                            </th>
                            <th className="text-right py-2 px-2 text-caption font-caption text-subtext-color">
                              Your Royalty
                            </th>
                            <th className="text-left py-2 px-2 text-caption font-caption text-subtext-color">
                              Transaction
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {royaltyStats.recentSales.map((sale) => (
                            <tr
                              key={sale.txHash}
                              className="border-b border-neutral-border"
                            >
                              <td className="py-3 px-2 text-body font-body text-default-font">
                                {new Date(sale.soldAt).toLocaleDateString()}{' '}
                                <span className="text-caption text-subtext-color">
                                  {new Date(sale.soldAt).toLocaleTimeString()}
                                </span>
                              </td>
                              <td className="py-3 px-2">
                                <a
                                  href={`/nfts/${sale.nftTokenId}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-brand-600 hover:text-brand-700"
                                >
                                  {sale.nftName || sale.nftTokenId.slice(0, 12) + '...'}
                                </a>
                              </td>
                              <td className="py-3 px-2 text-body font-body text-default-font text-right">
                                {sale.salePriceXrp.toLocaleString(undefined, {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 6,
                                })}{' '}
                                XRP
                              </td>
                              <td className="py-3 px-2 text-body font-body text-green-600 text-right">
                                +{sale.royaltyXrp.toLocaleString(undefined, {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 6,
                                })}{' '}
                                XRP
                              </td>
                              <td className="py-3 px-2">
                                <a
                                  href={`https://xrpscan.com/tx/${sale.txHash}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1 text-brand-600 hover:text-brand-700"
                                >
                                  <span className="text-body font-body">
                                    {sale.txHash.slice(0, 8)}...{sale.txHash.slice(-8)}
                                  </span>
                                  <FeatherExternalLink className="w-3 h-3" />
                                </a>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Empty State - only show if no recent sales */}
                {(!royaltyStats.recentSales || royaltyStats.recentSales.length === 0) && (
                  <div className="p-6 rounded-lg border border-neutral-border bg-default-background text-center">
                    <p className="text-body font-body text-subtext-color">
                      No secondary sales yet. Royalties will appear here when your NFTs are resold on the marketplace.
                    </p>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-12">
                <p className="text-body font-body text-subtext-color">
                  Failed to load royalty data
                </p>
                <Button
                  variant="neutral-secondary"
                  className="mt-4"
                  onClick={fetchRoyalties}
                >
                  Retry
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Submit Confirmation Modal */}
        {showSubmitConfirm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-default-background rounded-lg p-6 max-w-md w-full mx-4 border border-neutral-border">
              <h3 className="text-heading-3 font-heading-3 text-default-font mb-2">
                {launch.fee_paid ? 'Resubmit for Approval?' : 'Submit for Approval?'}
              </h3>
              <p className="text-body font-body text-subtext-color mb-4">
                {launch.fee_paid
                  ? 'Your collection will be reviewed by our team again. Please make sure you have addressed any previous feedback.'
                  : launch.collection_type === 'composable'
                    ? 'Once submitted, your collection will be reviewed by our team. You can still edit prices, royalties, and the allowlist during review. Traits cannot be changed after submission.'
                    : 'Once submitted, your collection will be reviewed by our team. You can still edit prices, royalties, and the allowlist during review. Assets cannot be changed after submission.'}
              </p>
              {launch.fee_paid ? (
                <div className="p-4 rounded-lg mb-6 border border-neutral-border bg-default-background">
                  <div className="flex items-center gap-2">
                    <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-body-bold font-body-bold text-default-font">
                      Platform fee already paid
                    </span>
                  </div>
                  <p className="text-caption font-caption text-subtext-color mt-1">
                    No additional payment required for resubmission.
                  </p>
                </div>
              ) : launch.calculated_fee && (
                <div className="p-4 rounded-lg mb-6 border border-neutral-border">
                  <h4 className="text-body-bold font-body-bold text-default-font mb-3">
                    Platform Fee Breakdown
                  </h4>
                  <div className="flex justify-between mb-2">
                    <span className="text-body font-body text-subtext-color">
                      Base fee
                    </span>
                    <span className="text-body font-body text-default-font">
                      {launch.calculated_fee.baseFeeXrp.toFixed(2)} XRP
                    </span>
                  </div>
                  <div className="flex justify-between mb-2">
                    <span className="text-body font-body text-subtext-color">
                      Per NFT fee ({launch.total_items} × {launch.calculated_fee.perNftFeeXrp} XRP)
                    </span>
                    <span className="text-body font-body text-default-font">
                      {(launch.total_items * launch.calculated_fee.perNftFeeXrp).toFixed(4)} XRP
                    </span>
                  </div>
                  <div className="border-t border-neutral-border mt-3 pt-3">
                    <div className="flex justify-between">
                      <span className="text-body-bold font-body-bold text-default-font">
                        Total
                      </span>
                      <span className="text-body-bold font-body-bold text-default-font">
                        {launch.calculated_fee.feeXrp.toFixed(2)} XRP
                      </span>
                    </div>
                  </div>
                </div>
              )}
              {paymentError && (
                <p className="text-body font-body text-red-500 mb-4">
                  {paymentError}
                </p>
              )}
              <div className="flex items-center justify-end gap-2">
                <Button
                  variant="neutral-secondary"
                  onClick={() => setShowSubmitConfirm(false)}
                  disabled={submitting}
                >
                  Cancel
                </Button>
                <Button onClick={handleSubmit} loading={submitting}>
                  {launch.fee_paid ? 'Resubmit' : 'Pay & Submit'}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Fee Payment Modal */}
        {showPaymentModal && feePaymentPayload && (
          <WalletSigningModal
            open={showPaymentModal}
            onClose={handlePaymentModalClose}
            title="Pay Platform Fee"
            payload={toSigningPayload(feePaymentPayload.signingIntent ?? feePaymentPayload.payload)}
            onSuccess={handlePaymentSuccess}
            onRejected={handlePaymentRejected}
            onExpired={handlePaymentExpired}
          />
        )}

        {/* Payment Confirmation Waiting Overlay */}
        {waitingForPayment && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
            <div className="bg-default-background rounded-xl p-8 max-w-md mx-4 text-center">
              <div className="flex justify-center mb-4">
                <LoadingSpinner />
              </div>
              <h3 className="text-heading-3 font-heading-3 text-default-font mb-2">
                Waiting for on-chain confirmation...
              </h3>
              <p className="text-body font-body text-subtext-color mb-4">
                Your payment has been signed. Waiting for the XRPL network to confirm the transaction.
                This usually takes a few seconds.
              </p>
              <p className="text-caption font-caption text-subtext-color">
                You can close this and the payment will still be detected automatically.
              </p>
              <Button
                variant="neutral-secondary"
                className="mt-4"
                onClick={() => {
                  if (paymentPollingRef.current) {
                    clearTimeout(paymentPollingRef.current);
                    paymentPollingRef.current = null;
                  }
                  setWaitingForPayment(false);
                  setFeePaymentPayload(null);
                  fetchLaunch();
                }}
              >
                Close
              </Button>
            </div>
          </div>
        )}

        {/* Authorization Modal */}
        {showAuthorizationModal && authorizationPayload && (
          <WalletSigningModal
            open={showAuthorizationModal}
            onClose={handleAuthorizationModalClose}
            title="Authorize Minter Wallet"
            payload={toSigningPayload(authorizationPayload.signingIntent ?? authorizationPayload.payload)}
            onSuccess={handleAuthorizationSuccess}
            onRejected={handleAuthorizationRejected}
            onExpired={handleAuthorizationExpired}
          />
        )}

        {/* Asset Detail Modal */}
        {selectedAsset && (
          <div
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
            onClick={() => setSelectedAsset(null)}
          >
            <div
              className="bg-default-background rounded-lg max-w-2xl w-full mx-4 max-h-[90vh] overflow-hidden border border-neutral-border flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="flex items-center justify-between p-4 border-b border-neutral-border">
                <h3 className="text-heading-3 font-heading-3 text-default-font">
                  {selectedAsset.name}
                </h3>
                <button
                  onClick={() => setSelectedAsset(null)}
                  className="text-subtext-color hover:text-default-font"
                >
                  ✕
                </button>
              </div>

              {/* Modal Content */}
              <div className="flex-1 overflow-y-auto p-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Image / 3D Model */}
                  <div className="aspect-square rounded-lg overflow-hidden border border-neutral-border">
                    {selectedAsset.original_image_key && is3DModelUrl(selectedAsset.original_image_key) ? (
                      <model-viewer
                        src={getModelViewerUrl(selectedAsset.original_image_key)}
                        alt={selectedAsset.name || `Token ${selectedAsset.token_number}`}
                        auto-rotate=""
                        camera-controls=""
                        shadow-intensity="1"
                        style={{ width: '100%', height: '100%', touchAction: 'pan-y' }}
                      />
                    ) : selectedAsset.original_image_key ? (
                      <img
                        src={selectedAsset.original_image_key}
                        alt={selectedAsset.name || `Token ${selectedAsset.token_number}`}
                        className="w-full h-full object-contain"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <span className="text-heading-1 font-heading-1 text-subtext-color">
                          {selectedAsset.token_number}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Details */}
                  <div className="space-y-4">
                    {selectedAsset.description && (
                      <div>
                        <h4 className="text-caption font-caption text-subtext-color mb-1">
                          Description
                        </h4>
                        <p className="text-body font-body text-default-font">
                          {selectedAsset.description}
                        </p>
                      </div>
                    )}

                    {/* Attributes */}
                    {Array.isArray(selectedAsset.attributes) && (selectedAsset.attributes as any[]).length > 0 && (
                      <div>
                        <h4 className="text-caption font-caption text-subtext-color mb-2">
                          Attributes
                        </h4>
                        <div className="grid grid-cols-2 gap-2">
                          {(selectedAsset.attributes as any[]).map((attr, i) => (
                            <div
                              key={i}
                              className="p-2 border border-neutral-border rounded"
                            >
                              <p className="text-caption font-caption text-subtext-color">
                                {String(attr?.trait_type || 'Unknown')}
                              </p>
                              <p className="text-body font-body text-default-font">
                                {String(attr?.value ?? '')}
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Status */}
                    <div>
                      <h4 className="text-caption font-caption text-subtext-color mb-1">
                        Status
                      </h4>
                      <span className="px-2 py-1 border border-neutral-border rounded text-body font-body text-default-font capitalize">
                        {selectedAsset.status}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Raw Metadata - Full width below the grid */}
                {selectedAsset.original_metadata != null && (
                  <div className="mt-6">
                    <h4 className="text-caption font-caption text-subtext-color mb-1">
                      Raw Metadata
                    </h4>
                    <pre className="p-3 border border-neutral-border rounded text-caption font-mono text-subtext-color whitespace-pre-wrap break-words">
                      {JSON.stringify(selectedAsset.original_metadata as object, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </DefaultPageLayout>
  );
};
